// Support loop — revision-controlled flow definition.
//
// This file is the single source of truth for /support/flow. To revise the flow,
// add a new entry to REVISIONS, bump CURRENT_REVISION, and tag any new node with
// `since: <rev>`. Nodes carrying `proposed: true` are not yet accepted — they
// render with a PROPOSED badge so the diagram never silently rewrites the
// process that was actually reviewed and signed off.

export const DOC = {
  id: 'NI-SUP-FLOW-001',
  title: 'Support loop — end-to-end flow',
  processOwner: 'Support process owner — to be assigned',
  systemOwner: 'RMA & QMS platform owner — to be assigned',
  approver: 'Quality manager — to be assigned',
  source: 'Support-loop flowchart (flows 1/4–4/4) + handwritten review comments from the printed copies, August 2026',
};

export const CURRENT_REVISION = 3;

export const REVISIONS = [
  {
    rev: 1,
    label: 'Rev 01',
    date: '2026-08-11',
    status: 'Superseded',
    author: 'Support process draft',
    summary: 'Baseline — the four-part support-loop flowchart as printed for review.',
    changes: [
      'Ten intake channels (#1.2–#1.11) each mapped to what the system captures (#1.12–#1.21).',
      'Single intake funnel (#2.0), case creation (#2.1) and a minimum-information gate (#2.2/#2.3).',
      'First-responder tier with a nine-item toolkit (#3.1A–#3.1I) and a solve/escalate decision (#3.1).',
      'Second-line tier with a twelve-item toolkit (#4.2A–#4.2L) and a remote-solve decision (#4.2).',
      'Escalation review (#5.1A–#5.1G), RMA and field-service paths, engineering/quality/supplier review.',
      'Four close states (#6.0–#6.3).',
    ],
  },
  {
    rev: 2,
    label: 'Rev 02',
    date: '2026-08-12',
    status: 'Issued',
    author: 'Printed-review markup',
    summary: 'Feedback loop added from the printed review — every close now feeds continuous improvement.',
    changes: [
      'Added the continuous-improvement hub (#7.0): every close (#6.0–#6.3) returns a quantified result.',
      'Added the prevention lever card (#7.1) — "we are obligated to help, but can reduce this by having better design, manuals, commissioning, partner training".',
      'Recorded the outcome target: fewer than 2 of every 1000 devices produced should end in accepted warranty.',
      'Recorded the intake ambition from the review: a stated share of all cases should be completed at intake / first line.',
      'Recorded the three-tier service line from the review (Partner/Us → Us/Sales Support → PD) as an open question.',
    ],
  },
  {
    rev: 3,
    label: 'Rev 03',
    date: '2026-08-12',
    status: 'Draft — for review',
    author: 'ISO alignment pass',
    summary: 'ISO 9001 / ISO 10002 / ISO 20000-1 alignment. Adds the steps those standards expect and closes three dead ends in the chart.',
    changes: [
      'Added acknowledgement to customer (#2.1A) — ISO 10002 requires receipt to be acknowledged.',
      'Added classification of type, severity, priority and safety flag at intake (#2.1B) — currently impact is not assessed until #5.1B, three tiers too late.',
      'Added a safety / regulatory interrupt (#2.1C → #2.1D) that bypasses the tier ladder.',
      'Added a dormancy close (#2.3A) so the #2.2 ⇄ #2.3 information loop cannot spin forever.',
      'Added not-confirmed branches (#3.3N, #4.4N) — Rev 01/02 had no path for a customer who does not accept the fix.',
      'Added customer-satisfaction capture on close (#6.4) — the Rev 02 loop measures us, not the customer.',
      'Added an installed-base impact check (#6.5) — ISO 9001 10.2 asks whether the same nonconformity exists elsewhere.',
      'Added known-error / FAQ publication (#7.3) so a closed case actually reduces the next case.',
      'Added a periodic support review (#7.2) feeding management review.',
    ],
  },
];

// Standards referenced by the ISO overlay. Clause numbers follow the edition
// named here — verify against the controlled copy before quoting them in an audit.
export const STANDARDS = {
  9001: { name: 'ISO 9001:2015', scope: 'Quality management systems — Requirements' },
  10002: { name: 'ISO 10002:2018', scope: 'Customer satisfaction — Guidelines for complaints handling' },
  10004: { name: 'ISO 10004:2018', scope: 'Customer satisfaction — Guidelines for monitoring and measuring' },
  20000: { name: 'ISO/IEC 20000-1:2018', scope: 'Service management system requirements' },
  31000: { name: 'ISO 31000:2018', scope: 'Risk management — Guidelines' },
  45001: { name: 'ISO 45001:2018', scope: 'Occupational health and safety management systems' },
  14001: { name: 'ISO 14001:2015', scope: 'Environmental management systems' },
};

export const LANES = {
  customer: { label: 'Customer', bg: '#20303F', border: '#5C9BD6', code: '#8FBFE8' },
  intake: { label: 'Intake system / automation', bg: '#1E332A', border: '#46BB89', code: '#6FD3A6' },
  first: { label: 'First responder (line 1)', bg: '#33301F', border: '#D9B44A', code: '#E8CD7E' },
  second: { label: 'Second line (line 2)', bg: '#362A1F', border: '#D98B4A', code: '#E8B183' },
  escalation: { label: 'Escalation owner', bg: '#3A2323', border: '#E06060', code: '#F09090' },
  rma: { label: 'RMA / field service', bg: '#1F3331', border: '#4FBFB4', code: '#7FD8CF' },
  engineering: { label: 'Engineering / quality / supplier', bg: '#2C2637', border: '#A98BDB', code: '#C7B2EC' },
  partner: { label: 'Partner / distributor', bg: '#2B2C2D', border: '#939597', code: '#B9BBBD' },
  closed: { label: 'Closed / end state', bg: '#343637', border: 'rgba(255,255,255,0.30)', code: '#C9CCCB' },
  improve: { label: 'Continuous improvement', bg: '#172A24', border: '#46BB89', code: '#6FD3A6' },
  email: { label: 'Automatic email to customer', bg: '#2E2438', border: '#CE93D8', code: '#DCB4E4' },
  mttr: { label: 'MTTR measurement window', bg: '#291F33', border: '#A366C4', code: '#C79BE0' },
  warning: { label: 'Warranty risk flag', bg: '#3D2020', border: '#EF5350', code: '#FF9A98' },
  refurb: { label: 'Refurbishment track', bg: '#181C1D', border: '#6E7476', code: '#9AA1A3' },
  odoo: { label: 'Odoo Helpdesk (support loop)', bg: '#2B2438', border: '#8E7CC3', code: '#BCA9E0' },
  metric: { label: 'Measured automatically', bg: '#132A33', border: '#3FA7C4', code: '#8ED4E8' },
};

// The three-tier service line recorded in the printed review. The handwriting is
// legible but the mapping is an inference — kept flagged until confirmed.
export const SERVICE_LINES = [
  { tier: 'Line 1', owner: 'Partner / Us', lane: 'first', note: 'Partner takes first contact where one exists; otherwise North Invent support answers directly.' },
  { tier: 'Line 2', owner: 'Us / Sales Support', lane: 'second', note: 'North Invent support with sales support for commercial and warranty context.' },
  { tier: 'Line 3', owner: 'PD', lane: 'engineering', note: 'Product Development — read from the review as "PD". Confirm scope and who is on call.' },
];

export const TARGETS = [
  {
    metric: '< 2 / 1000',
    label: 'Devices reaching accepted warranty',
    detail: 'Of every 1000 devices produced, fewer than 2 should end up in accepted warranty. This flow exists to show what to fix to reach that number, and to keep cases from turning into RMAs.',
    source: 'Printed review, red annotation',
  },
  {
    metric: '100%',
    label: 'Cases completed inside the support system',
    detail: 'Read from the review annotation "we want ___% of cases to be done here" next to #2.0. Read as 100% — i.e. every case is registered and closed in the system rather than in an inbox. Confirm the intended number and whether "here" means the system, intake, or first line.',
    source: 'Printed review, red annotation',
    unconfirmed: true,
  },
];

// ---------------------------------------------------------------------------
// Nodes
// ---------------------------------------------------------------------------

const N = {};
const node = (def) => { N[def.id] = def; return def; };

// --- Phase 1: channels & intake -------------------------------------------

node({
  id: '1.0', code: '#1.0', title: 'Customer needs help', kind: 'start', lane: 'customer', since: 1,
  detail: {
    what: 'Something is wrong, unclear, or not working for a customer, partner or internal user. Demand arrives whether or not we are ready for it.',
    owner: 'Customer / partner',
    rules: [
      'We are obligated to help. The volume arriving here is not fully in our control — but it is reducible (see #7.1).',
      'Demand at this point is the input to every metric in this flow. It is not a failure signal on its own.',
    ],
    iso: [
      { ref: '10002:4.5', why: 'Accessibility — it must be easy to find out how and where to complain or ask for help.' },
      { ref: '9001:8.2.1 a)', why: 'Customer communication includes providing information about the product and its service.' },
    ],
  },
});

node({
  id: '1.1', code: '#1.1', title: 'Choose help channel', kind: 'decision', lane: 'customer', since: 1,
  detail: {
    what: 'The customer picks whichever route is easiest for them. We do not get to choose this — so every route below has to land in the same place.',
    owner: 'Customer / partner',
    rules: [
      'No channel may be a dead end. If a route cannot create a case, it is not a support channel.',
      'Channel choice must not change the level of service, only the amount of typing.',
    ],
    watch: 'The two channels marked in the review (#1.2 QR/NFC and #1.5 email) are the ones most likely to be used and the ones with the weakest structure today.',
    iso: [
      { ref: '10002:4.4', why: 'Transparency — how to raise an issue must be publicly and clearly communicated.' },
    ],
  },
});

const channel = (id, code, title, lane, capture, detail) => {
  node({ id, code, title, kind: 'step', lane, since: 1, detail });
  node(capture);
  return { channel: id, capture: capture.id };
};

const CHANNEL_PAIRS = [
  channel('1.2', '#1.2', 'Product QR / NFC support page', 'customer',
    {
      id: '1.12', code: '#1.12', title: 'Product identity auto-filled', kind: 'step', lane: 'intake', since: 1,
      detail: {
        what: 'Scanning the tag on the unit resolves the service tag, product number, build order and warranty position before the customer types anything.',
        owner: 'Platform (device page / NFC tag)',
        outputs: ['Service tag', 'Product number', 'Scan date and warranty status', 'Build-order and production history link'],
        rules: ['This is the cheapest possible intake — zero identification effort and zero transcription error.'],
        iso: [{ ref: '9001:8.5.2', why: 'Identification and traceability — the unit is identified from its own tag rather than from customer recollection.' }],
      },
    },
    {
      what: 'The customer scans the QR or taps the NFC tag on the device itself and lands on that unit\'s support entry point.',
      owner: 'Customer',
      rules: ['Preferred channel. Every other channel costs us identification work that this one does for free.'],
      watch: 'Marked in the review. Needs the unregistered-service-tag fallback to behave sensibly rather than dead-ending.',
      flagNote: 'Marked ⚑ on the printed review — confirm what was intended here.',
      iso: [{ ref: '10002:4.5', why: 'Accessibility — the route to help is physically on the product.' }],
    }),

  channel('1.3', '#1.3', 'Customer support portal', 'customer',
    {
      id: '1.13', code: '#1.13', title: 'Customer / company identified', kind: 'step', lane: 'intake', since: 1,
      detail: {
        what: 'A signed-in portal session identifies the company, its fleet and its agreement, so the case starts with commercial context attached.',
        owner: 'Platform (organisation records)',
        outputs: ['Company / organisation', 'Devices owned', 'Warranty agreement in force', 'Prior case history'],
        iso: [{ ref: '9001:8.2.1 b)', why: 'Handling enquiries against the right contract and its changes.' }],
      },
    },
    {
      what: 'A signed-in customer raises or follows a case from the portal.',
      owner: 'Customer',
      rules: ['Best channel for customers with a fleet — they can see their own units and history.'],
    }),

  channel('1.4', '#1.4', 'Web support form', 'customer',
    {
      id: '1.14', code: '#1.14', title: 'Structured support form completed', kind: 'step', lane: 'intake', since: 1,
      detail: {
        what: 'A public form collects the mandatory fields up front so the case does not need a round trip before work can start.',
        owner: 'Platform (support form)',
        outputs: ['Required-information set (see #3.1C)', 'Issue category', 'Attachments'],
        rules: ['The form is where the minimum-information rule (#2.2) is enforced cheaply — before a case exists, not after.'],
        iso: [{ ref: '10002:8.2', why: 'Receipt of complaints — capturing the information needed to handle it at the point of receipt.' }],
      },
    },
    {
      what: 'An unauthenticated customer fills in the public support form.',
      owner: 'Customer',
      rules: ['Only channel available to a customer with no portal account and no readable tag.'],
    }),

  channel('1.5', '#1.5', 'Email to support address', 'customer',
    {
      id: '1.15', code: '#1.15', title: 'Email converted to support case draft', kind: 'step', lane: 'intake', since: 1,
      detail: {
        what: 'Mail to the shared support address is turned into a case draft. The mail thread becomes an attachment to the case, not a parallel process.',
        owner: 'Support (whoever picks it up) + platform',
        outputs: ['Case draft with the original mail attached', 'Sender resolved to a company where possible'],
        rules: [
          'Rule that makes this work: the receiver registers the case. Never bounce a customer to "please use the form".',
          'Once the case exists, the answer goes out from the case — not as a private reply from one inbox.',
        ],
        watch: 'Highest-risk channel. Unstructured, arrives in one person\'s inbox, and is where cases historically go dark. Marked on the printed review.',
        iso: [
          { ref: '10002:8.2', why: 'Receipt — a complaint arriving by any means must be recorded.' },
          { ref: '9001:7.5.3', why: 'Control of documented information — the record must not live only in a personal mailbox.' },
        ],
      },
    },
    {
      what: 'The customer emails the shared support address.',
      owner: 'Customer',
      watch: 'Marked ⚑ on the printed review. This is the channel the new way of working has to actively displace.',
      flagNote: 'Marked ⚑ on the printed review — confirm what was intended here.',
    }),

  channel('1.6', '#1.6', 'Chatbot / guided support assistant', 'customer',
    {
      id: '1.16', code: '#1.16', title: 'Chatbot collects structured information', kind: 'step', lane: 'intake', since: 1,
      detail: {
        what: 'A guided assistant asks the required questions in order, resolves the obvious known issues itself, and only creates a case for what it cannot close.',
        owner: 'Platform (assistant) + support process owner for the script',
        outputs: ['Structured answer set', 'Attempted self-service steps and their result', 'Case only where self-service failed'],
        rules: ['Anything this closes never becomes a case — this is the main lever on first-line load.'],
        iso: [{ ref: '9001:7.1.6', why: 'Organizational knowledge made directly available at the point of need.' }],
      },
    },
    {
      what: 'The customer uses a guided assistant instead of writing free text.',
      owner: 'Customer',
      rules: ['Only as good as the known-issue content behind it (#7.3). Without that it is a slower form.'],
    }),

  channel('1.7', '#1.7', 'Phone call', 'customer',
    {
      id: '1.17', code: '#1.17', title: 'Support person logs phone call', kind: 'step', lane: 'first', since: 1,
      detail: {
        what: 'The person who takes the call writes the case. Manual, and the only channel where the record depends entirely on a human remembering to make it.',
        owner: 'Support person who answered',
        rules: [
          'The case is logged during or immediately after the call — not at end of day.',
          'A phone call with no case did not happen, as far as the process is concerned.',
        ],
        watch: 'Weakest link for traceability. Urgent calls are exactly the ones most likely to go unlogged, and exactly the ones that most need a record.',
        iso: [{ ref: '10002:8.3', why: 'Tracking — a complaint must be traceable from receipt to resolution regardless of how it arrived.' }],
      },
    },
    {
      what: 'The customer calls a person directly.',
      owner: 'Customer',
      rules: ['Fastest route for the customer, most expensive route for us.'],
    }),

  channel('1.8', '#1.8', 'Live chat', 'customer',
    {
      id: '1.18', code: '#1.18', title: 'Live chat transcript saved', kind: 'step', lane: 'intake', since: 1,
      detail: {
        what: 'The transcript is attached to the case automatically, so the conversation survives the session.',
        owner: 'Platform (chat)',
        outputs: ['Full transcript on the case', 'Agent and timestamps'],
        iso: [{ ref: '9001:7.5.3', why: 'Retention of the record of the exchange.' }],
      },
    },
    { what: 'The customer opens a live chat session.', owner: 'Customer' }),

  channel('1.9', '#1.9', 'Partner / distributor contact', 'partner',
    {
      id: '1.19', code: '#1.19', title: 'Partner creates or forwards structured case', kind: 'step', lane: 'partner', since: 1,
      detail: {
        what: 'The partner has already done line-1 work. The case must carry what they tried, not just what the end customer said.',
        owner: 'Partner / distributor',
        outputs: ['Partner\'s own findings and steps already taken', 'End-customer identity and site', 'Partner case reference'],
        rules: [
          'A partner case that arrives without the partner\'s troubleshooting is an information-incomplete case (#2.2).',
          'Partner quality of input is a training outcome, not a personality trait — see #7.1 partner training.',
        ],
        iso: [{ ref: '9001:8.4', why: 'Control of externally provided processes — the partner performs part of our line-1 process on our behalf.' }],
      },
    },
    {
      what: 'A partner or distributor raises a case on behalf of their end customer.',
      owner: 'Partner',
      rules: ['Line 1 in the tiered model is "Partner / Us" — so for partner-served accounts this is the normal entry point.'],
    }),

  channel('1.10', '#1.10', 'Internal North Invent contact', 'customer',
    {
      id: '1.20', code: '#1.20', title: 'Internal person creates case on customer behalf', kind: 'step', lane: 'first', since: 1,
      detail: {
        what: 'A customer asks a salesperson, a project engineer, or anyone else they happen to know. That person registers the case rather than solving it privately.',
        owner: 'Whichever internal person was contacted',
        rules: [
          'This is the single most important behaviour change in the rollout: being asked directly does not exempt you from registering it.',
          'You may still help. You may not be the only record that it happened.',
        ],
        watch: 'The path that most often produces invisible support work and unpredictable load on engineering.',
        iso: [{ ref: '10002:8.2', why: 'Receipt — however the customer reaches us, the case is recorded.' }],
      },
    },
    {
      what: 'The customer contacts someone they already know inside North Invent.',
      owner: 'Customer',
      rules: ['Cannot be prevented and should not be. It just has to end in a case.'],
    }),

  channel('1.11', '#1.11', 'Existing case follow-up', 'customer',
    {
      id: '1.21', code: '#1.21', title: 'Existing support case updated', kind: 'step', lane: 'intake', since: 1,
      detail: {
        what: 'A reply, a new symptom or a chase lands on the case that already exists instead of starting a second one.',
        owner: 'Platform + case owner',
        rules: [
          'Duplicate cases destroy the recurrence data that #7.0 depends on. Merge on sight.',
          'A returning problem on the same unit is a link, not a fresh start — history is the whole point.',
        ],
        iso: [{ ref: '10002:8.3', why: 'Tracking — the customer can be told the status of their existing complaint.' }],
      },
    },
    { what: 'The customer comes back about a case that is already open or recently closed.', owner: 'Customer' }),
];

node({
  id: '2.0', code: '#2.0', title: 'Support intake', kind: 'step', lane: 'intake', since: 1,
  detail: {
    what: 'The single funnel. Everything from #1.12–#1.21 converges here, which is what makes one set of numbers possible across ten different channels.',
    owner: 'Support process owner',
    inputs: ['All channel captures #1.12–#1.21'],
    outputs: ['One queue', 'One counting point', 'One place the customer can be told the status'],
    rules: [
      'The review target sits on this node: essentially every case should be completed inside the system rather than in an inbox.',
      'If a case does not pass through here, it is invisible to every metric downstream — including the < 2/1000 target.',
    ],
    iso: [
      { ref: '10002:8.2', why: 'Receipt of complaints.' },
      { ref: '20000:8.6.1', why: 'Incident management — incidents are recorded and classified in one controlled place.' },
    ],
  },
});

node({
  id: '2.1', code: '#2.1', title: 'Support case created', kind: 'step', lane: 'intake', since: 1,
  detail: {
    what: 'The case record exists, has an ID the customer can quote, and has one named owner.',
    owner: 'Support process owner',
    outputs: ['Case ID', 'Named case owner', 'Timestamped audit trail from this point on'],
    rules: [
      'One named owner at all times. A group mailbox is not an owner.',
      'The case ID is the reference used in every later conversation, internal or external.',
    ],
    iso: [
      { ref: '9001:7.5.2', why: 'Created documented information is identified and described.' },
      { ref: '9001:5.3', why: 'Responsibility and authority for the case is assigned to a person.' },
    ],
  },
});

node({
  id: '2.1A', code: '#2.1A', title: 'Acknowledge receipt to customer', kind: 'step', lane: 'intake', since: 3, proposed: true,
  detail: {
    what: 'An immediate acknowledgement goes back to the customer with the case ID and when they can expect a real answer.',
    owner: 'Platform (automatic) — wording owned by support process owner',
    outputs: ['Case ID sent to customer', 'Stated target for first substantive response'],
    rules: [
      'Automatic, on every channel, within minutes.',
      'An acknowledgement is not an answer. It stops the customer chasing, it does not stop the clock.',
    ],
    why: 'Rev 01/02 went from case creation straight to screening. The customer had no confirmation that anything had been received, which is the most common reason a customer escalates by phone.',
    iso: [
      { ref: '10002:8.4', why: 'Acknowledgement of complaints — receipt should be acknowledged to the complainant immediately.' },
      { ref: '10002:4.6', why: 'Responsiveness.' },
      { ref: '9001:8.2.1 b)', why: 'Handling enquiries.' },
    ],
  },
});

node({
  id: '2.1B', code: '#2.1B', title: 'Classify: type, severity, priority, safety flag', kind: 'step', lane: 'intake', since: 3, proposed: true,
  detail: {
    what: 'Set four things before any technical work: is it a question or a complaint, how bad is the effect on the customer, how urgent, and is safety involved.',
    owner: 'First responder at pickup',
    outputs: ['Request type (question / fault / complaint / warranty claim)', 'Severity (effect on the customer\'s operation)', 'Priority (severity × urgency)', 'Safety / regulatory flag'],
    rules: [
      'Severity describes the customer\'s situation, not our workload.',
      'A complaint carries obligations a question does not — it must be distinguishable in reporting.',
      'Priority drives queue order. Without it, order of arrival wins and the vessel that is down waits behind a documentation query.',
    ],
    why: 'Rev 01/02 first assessed customer impact at #5.1B — after two full tiers. That means the flow could not tell a stopped installation from a cosmetic query until it had already spent the time of both support tiers.',
    iso: [
      { ref: '10002:8.5', why: 'Initial assessment — each complaint assessed against criteria such as severity, safety implication, complexity and impact.' },
      { ref: '20000:8.6.1', why: 'Incident management — incidents shall be classified and prioritized.' },
      { ref: '20000:8.3.3', why: 'Service level management — priority is what makes a response target meaningful.' },
    ],
  },
});

node({
  id: '2.1C', code: '#2.1C', title: 'Safety or regulatory issue?', kind: 'decision', lane: 'escalation', since: 3, proposed: true,
  detail: {
    what: 'A hard gate. Anything with a possible safety, fire, electrical or regulatory dimension leaves the tier ladder immediately.',
    owner: 'First responder, with quality manager as the standing decision authority',
    rules: [
      'When unsure, the answer is yes. Over-escalation here is cheap; under-escalation is not.',
      'The tier ladder is an efficiency mechanism. It must not delay a safety signal.',
    ],
    why: 'Rev 01/02 had one route for everything. A potential safety issue would have queued behind ordinary first-line work.',
    iso: [
      { ref: '10002:8.5', why: 'Initial assessment explicitly includes safety implications and the need for immediate action.' },
      { ref: '9001:8.7', why: 'Control of nonconforming outputs.' },
      { ref: '31000:6.4', why: 'Risk assessment — a route proportionate to consequence.' },
    ],
  },
});

node({
  id: '2.1D', code: '#2.1D', title: 'Immediate safety escalation', kind: 'step', lane: 'escalation', since: 3, proposed: true,
  detail: {
    what: 'Straight to quality manager and Product Development, in parallel with informing the customer. Includes deciding whether other units in the field are exposed.',
    owner: 'Quality manager',
    outputs: ['Same-day notification to QM + PD', 'Containment advice to the customer', 'Installed-base exposure assessment (#6.5)', 'Decision on whether a field notice is required'],
    rules: [
      'Runs in parallel with the normal case, it does not replace it.',
      'The exposure question — "how many other units could do this?" — is answered here, not after the repair.',
    ],
    iso: [
      { ref: '9001:8.7.1', why: 'Action on nonconforming output, including informing the customer.' },
      { ref: '9001:10.2.1', why: 'Evaluate whether similar nonconformities exist or could potentially occur.' },
    ],
  },
});

node({
  id: '2.2', code: '#2.2', title: 'Minimum information complete?', kind: 'decision', lane: 'intake', since: 1,
  detail: {
    what: 'Gate: can this case actually be worked, or is it a title with no content?',
    owner: 'First responder',
    rules: [
      'Checked against the required-information checklist (#3.1C) — one list, not per-person judgement.',
      'Structured channels (#1.4, #1.6) should pass this automatically. Email and phone are where it bites.',
    ],
    iso: [{ ref: '10002:8.6', why: 'Investigation — relevant circumstances and information must be gathered.' }],
  },
});

node({
  id: '2.3', code: '#2.3', title: 'Request missing information', kind: 'step', lane: 'first', since: 1,
  detail: {
    what: 'One consolidated request for everything that is missing, then back to the gate.',
    owner: 'Case owner',
    rules: [
      'Ask once, ask for everything. Serial round trips are the main cause of slow cases that were never technically difficult.',
      'Tell the customer why each item is needed — it materially improves what comes back.',
    ],
    watch: 'Rev 01/02 left this loop unbounded: #2.2 → #2.3 → re-check → #2.2 with no exit if the customer never replies. #2.3A closes it.',
    iso: [{ ref: '10002:4.15', why: 'Timeliness.' }],
  },
});

node({
  id: '2.3A', code: '#2.3A', title: 'No reply → close as insufficient information', kind: 'end', lane: 'closed', since: 3, proposed: true,
  detail: {
    what: 'After a defined dormant period with two chases and no response, the case closes with a stated reason and an easy route to reopen.',
    owner: 'Case owner',
    outputs: ['Close reason: insufficient information', 'Reopen link kept live', 'Counted separately in reporting'],
    rules: [
      'Set the dormancy period explicitly (a working-week is a reasonable default) — an undefined period means cases sit open forever.',
      'Closing for silence is not a resolution and must never be counted as one.',
    ],
    why: 'Closes the unbounded #2.2 ⇄ #2.3 loop and stops dormant cases from inflating the open queue and distorting the intake target.',
    iso: [{ ref: '10002:8.9', why: 'Closing complaints — a defined basis for closure.' }],
  },
});

// --- Phase 2: first responder ---------------------------------------------

node({
  id: '3.0', code: '#3.0', title: 'First responder screening', kind: 'step', lane: 'first', since: 1,
  detail: {
    what: 'Line 1 picks the case up and works the toolkit in order. The aim is to close it here.',
    owner: 'First responder — Partner / Us in the tiered model',
    rules: [
      'Working the toolkit in order is what makes the escalation, if it happens, useful to line 2.',
      'Every step skipped here reappears as a question from line 2.',
    ],
    iso: [
      { ref: '9001:7.2', why: 'Competence — the people doing this must be demonstrably able to.' },
      { ref: '20000:8.6.1', why: 'Incident management — resolution at first point of contact where possible.' },
    ],
  },
});

node({
  id: '3.1TK', code: '#3.1A–#3.1I', title: 'First responder toolkit', kind: 'toolkit', lane: 'first', since: 1,
  items: [
    { code: '#3.1A', title: 'Service tag / product lookup', note: 'What exactly is this unit, when was it built, is it in warranty.' },
    { code: '#3.1B', title: 'Customer / partner / case history lookup', note: 'Has this customer — or this unit — been here before?' },
    { code: '#3.1C', title: 'Required information checklist', note: 'The single definition of "workable case" used by the #2.2 gate.' },
    { code: '#3.1D', title: 'Issue category and symptom checklist', note: 'Puts the case into a class that can be counted and compared.' },
    { code: '#3.1E', title: 'Photo / video / attachment evidence guide', note: 'Tells the customer what to photograph. Removes a whole round trip.' },
    { code: '#3.1F', title: 'FAQ / product manual / knowledge base', note: 'Fed by #7.3. If this is thin, line 1 cannot close anything.' },
    { code: '#3.1G', title: 'Known issue / service bulletin lookup', note: 'Recognising a known failure mode is the single biggest time saver in the flow.' },
    { code: '#3.1H', title: 'Basic troubleshooting guides', note: 'The scripted steps line 2 will otherwise ask whether you did.' },
    { code: '#3.1I', title: 'Communication templates and case notes', note: 'Consistent customer wording, and notes written for the next reader.' },
  ],
  detail: {
    what: 'Nine tools worked in order. Together they are the definition of what "line 1 tried" means.',
    owner: 'Support process owner maintains the toolkit; first responder uses it',
    rules: [
      'The order matters: identify → check history → complete → classify → evidence → known answers → try → document.',
      'The toolkit is the training curriculum for a new first responder.',
    ],
    iso: [
      { ref: '9001:7.1.6', why: 'Organizational knowledge is determined, maintained and made available.' },
      { ref: '9001:7.2', why: 'Competence.' },
    ],
  },
});

node({
  id: '3.1', code: '#3.1', title: 'Can first responder solve it?', kind: 'decision', lane: 'first', since: 1,
  detail: {
    what: 'The main load-bearing decision in the flow. The share of cases answered yes here is the headline efficiency number.',
    owner: 'First responder',
    rules: [
      'Escalating is not failure — escalating without the toolkit worked is.',
      'A no must state what was tried and what is unknown, not just that it is hard.',
    ],
    iso: [{ ref: '20000:8.6.1', why: 'Escalation criteria form part of incident management.' }],
  },
});

node({
  id: '3.2', code: '#3.2', title: 'Document first-line solution', kind: 'step', lane: 'first', since: 1,
  detail: {
    what: 'Write down what was actually wrong and what actually fixed it — in terms the next reader can reuse.',
    owner: 'First responder',
    outputs: ['Cause', 'Fix', 'Whether it is a candidate for #7.3 publication'],
    rules: [
      '"Resolved" is not a resolution. "Backlight connector reseated after transit vibration" is.',
      'This text is the raw material for the FAQ and known-issue content that lets the next case close faster.',
    ],
    iso: [{ ref: '9001:7.5.2', why: 'Documented information created and updated.' }],
  },
});

node({
  id: '3.3', code: '#3.3', title: 'Customer confirms solved', kind: 'step', lane: 'customer', since: 1,
  detail: {
    what: 'The customer — not us — says it is fixed.',
    owner: 'Case owner obtains it; customer gives it',
    rules: ['Our opinion that it is fixed is not a close condition.'],
    watch: 'Rev 01/02 had no branch for "customer does not confirm". #3.3N adds it.',
    iso: [{ ref: '10002:8.8', why: 'Communicating the decision to the complainant.' }],
  },
});

node({
  id: '3.3N', code: '#3.3N', title: 'Not confirmed → reopen and re-escalate', kind: 'step', lane: 'first', since: 3, proposed: true,
  detail: {
    what: 'The customer says it is not fixed. The case reopens with the failed attempt recorded and goes to line 2 — it does not restart at line 1.',
    owner: 'Case owner',
    outputs: ['Failed-fix record', 'Direct escalation to #4.0', 'Reopen flag for reporting'],
    rules: [
      'Never re-run the same first-line script on a customer who has already told you it did not work.',
      'A reopened case is a signal about our diagnosis, and should be counted as one.',
    ],
    why: 'Rev 01/02 assumed the customer always confirms. In practice a non-confirmation was falling out of the process entirely — usually back into email.',
    iso: [
      { ref: '10002:8.8', why: 'Where the decision is not accepted by the complainant, the complaint remains open.' },
      { ref: '9001:10.2.1', why: 'React to the nonconformity, including dealing with the consequences.' },
    ],
  },
});

node({
  id: '4.0', code: '#4.0', title: 'Escalate to second line', kind: 'step', lane: 'first', since: 1,
  detail: {
    what: 'Formal handover of ownership from line 1 to line 2.',
    owner: 'First responder → second line',
    rules: ['Ownership transfers to a named person, not to a queue. Otherwise the case pauses at the handover.'],
    iso: [{ ref: '20000:8.6.1', why: 'Escalation as part of incident management.' }],
  },
});

node({
  id: '4.0A', code: '#4.0A', title: 'First responder escalation summary', kind: 'step', lane: 'first', since: 1,
  detail: {
    what: 'A short written statement: what the symptom is, what was tried, what the result was, and what line 1 believes is going on.',
    owner: 'First responder',
    outputs: ['Symptom', 'Steps taken and outcomes', 'Working hypothesis', 'The specific question line 2 is being asked'],
    rules: [
      'Naming the actual question — not just forwarding the case — is what makes line 2 fast.',
      'If line 2 has to reconstruct the story, the escalation has cost more than it saved.',
    ],
    iso: [{ ref: '9001:7.5.2', why: 'Adequate documented information at handover.' }],
  },
});

node({
  id: '4.0B', code: '#4.0B', title: 'Attach evidence and troubleshooting history', kind: 'step', lane: 'first', since: 1,
  detail: {
    what: 'Photos, video, logs, transcripts and prior cases go onto the case before it moves.',
    owner: 'First responder',
    rules: ['Evidence gathered later is worse evidence — the unit has usually been touched by then.'],
    iso: [{ ref: '9001:7.5.3', why: 'Documented information available where and when needed.' }],
  },
});

// --- Phase 3: second line -------------------------------------------------

node({
  id: '4.1', code: '#4.1', title: 'Second line technical review', kind: 'step', lane: 'second', since: 1,
  detail: {
    what: 'Line 2 opens the unit\'s full production and service record and works the twelve-item toolkit.',
    owner: 'Second line — Us / Sales Support in the tiered model',
    rules: ['This tier exists because the data exists. If the record is not consulted, the tier adds delay rather than capability.'],
    iso: [
      { ref: '9001:8.5.5', why: 'Post-delivery activities.' },
      { ref: '9001:8.5.2', why: 'Identification and traceability of the specific unit.' },
    ],
  },
});

node({
  id: '4.2TK', code: '#4.2A–#4.2L', title: 'Second line toolkit', kind: 'toolkit', lane: 'second', since: 1,
  items: [
    { code: '#4.2A', title: 'Full service tag / product history', note: 'Everything that has ever happened to this unit.' },
    { code: '#4.2B', title: 'BOM / configuration / component traceability', note: 'Which parts, from which supplier, in this exact unit.' },
    { code: '#4.2C', title: 'Production test records', note: 'Did it pass, and how close to the limit?' },
    { code: '#4.2D', title: 'Firmware / software / release note review', note: 'What version is on it, and what changed in that version.' },
    { code: '#4.2E', title: 'Calibration / display / touch records', note: 'The measured values from build, not the nominal ones.' },
    { code: '#4.2F', title: 'Technical documentation and service manuals', note: 'Controlled documents, correct revision for this build.' },
    { code: '#4.2G', title: 'Remote diagnostic session / controlled customer test', note: 'A defined test the customer runs while you watch.' },
    { code: '#4.2H', title: 'Diagnostic tools and scripts', note: 'Repeatable data collection rather than ad-hoc questions.' },
    { code: '#4.2I', title: 'Previous RMA / known failure mode comparison', note: 'Have we seen this exact signature before?' },
    { code: '#4.2J', title: 'Batch / lot / firmware correlation check', note: 'Turns one case into a population question. Feeds #6.5.' },
    { code: '#4.2K', title: 'Engineering / quality signal check', note: 'Open NCRs, ECOs and supplier issues that could explain it.' },
    { code: '#4.2L', title: 'Technical response and documentation', note: 'The written technical answer, reusable by line 1 next time.' },
  ],
  detail: {
    what: 'Twelve tools that turn a customer symptom into a statement about a specific unit, a specific build and a specific component.',
    owner: 'Second line',
    rules: [
      '#4.2J is the highest-value step for prevention — it is where a single complaint becomes a batch finding.',
      '#4.2L is what stops line 2 answering the same question forever: the answer goes back into line 1\'s toolkit.',
    ],
    iso: [
      { ref: '9001:8.5.2', why: 'Identification and traceability.' },
      { ref: '9001:8.6', why: 'Evidence of conformity at release is available for review.' },
      { ref: '20000:8.6.3', why: 'Problem management — finding the underlying cause across incidents.' },
    ],
  },
});

node({
  id: '4.2', code: '#4.2', title: 'Can second line solve remotely?', kind: 'decision', lane: 'second', since: 1,
  detail: {
    what: 'Can this be fixed without the unit coming back and without anyone travelling?',
    owner: 'Second line',
    rules: [
      'Remote is dramatically cheaper than either RMA or field service — for us and for the customer\'s uptime.',
      'A no here commits real money, so the reasoning gets written down.',
    ],
    iso: [{ ref: '31000:6.4', why: 'Weighing consequence and cost of the available options.' }],
  },
});

node({
  id: '4.3', code: '#4.3', title: 'Provide remote solution', kind: 'step', lane: 'second', since: 1,
  detail: {
    what: 'Configuration change, firmware update, guided procedure or part sent for customer fitting.',
    owner: 'Second line',
    rules: ['Record the exact change made — a later case on the same unit needs to know its current state.'],
    iso: [{ ref: '9001:8.5.5', why: 'Post-delivery activity, performed under controlled conditions.' }],
  },
});

node({
  id: '4.4', code: '#4.4', title: 'Customer confirms solved', kind: 'step', lane: 'customer', since: 1,
  detail: {
    what: 'The customer confirms the remote fix worked.',
    owner: 'Case owner obtains it; customer gives it',
    watch: 'Same missing branch as #3.3 in Rev 01/02 — #4.4N adds it.',
    iso: [{ ref: '10002:8.8', why: 'Communicating the decision.' }],
  },
});

node({
  id: '4.4N', code: '#4.4N', title: 'Not confirmed → escalation decision', kind: 'step', lane: 'second', since: 3, proposed: true,
  detail: {
    what: 'The remote fix did not hold. The case goes to #5.0 with the failed remote attempt on the record.',
    owner: 'Second line',
    rules: [
      'A failed remote fix is strong evidence for the RMA-versus-field-service decision — it narrows the fault.',
      'Do not attempt the same remote fix twice without a new reason.',
    ],
    why: 'Rev 01/02 dead-ended on non-confirmation at both tiers. This is the second of the two gaps.',
    iso: [{ ref: '10002:8.8', why: 'Complaint remains open where the response is not accepted.' }],
  },
});

// --- Phase 4: escalation, RMA & field service -----------------------------

node({
  id: '5.0', code: '#5.0', title: 'Escalation decision', kind: 'step', lane: 'escalation', since: 1,
  detail: {
    what: 'The point where the case stops being a support conversation and becomes a logistics and cost decision.',
    owner: 'Escalation owner',
    rules: ['One named escalation owner. This decision commits shipping, spares, travel or engineering time.'],
    iso: [
      { ref: '9001:8.7', why: 'Control of nonconforming outputs.' },
      { ref: '9001:6.1', why: 'Risk-based thinking applied to the chosen route.' },
    ],
  },
});

node({
  id: '5.1TK', code: '#5.1A–#5.1G', title: 'Escalation review', kind: 'toolkit', lane: 'escalation', since: 1,
  items: [
    { code: '#5.1A', title: 'Review full support case summary', note: 'Everything both tiers established, read as one story.' },
    { code: '#5.1B', title: 'Review customer impact and urgency', note: 'Rev 03 moves this judgement to #2.1B — here it is confirmed, not discovered.' },
    { code: '#5.1C', title: 'Check unit availability for return', note: 'Can the customer actually release the unit?' },
    { code: '#5.1D', title: 'Check field access feasibility', note: 'Vessel, site, offshore, permits, travel time.' },
    { code: '#5.1E', title: 'Check replacement / spare availability', note: 'No spare means no swap, whatever the decision matrix says.' },
    { code: '#5.1F', title: 'Review warranty / commercial context', note: 'Who pays, under which agreement.' },
    { code: '#5.1G', title: 'RMA vs field service decision matrix', note: 'The written rule, so the same case gets the same answer from anyone.' },
  ],
  detail: {
    what: 'Seven checks that make the RMA-versus-field-service choice a decision rather than a preference.',
    owner: 'Escalation owner',
    rules: [
      '#5.1G is the control. Without a written matrix this decision drifts with whoever is on duty.',
      '#5.1C and #5.1E are hard constraints and are worth checking before the rest.',
    ],
    iso: [
      { ref: '9001:8.7.1', why: 'Determining action on nonconforming output.' },
      { ref: '9001:8.2.1 e)', why: 'Contingency actions where relevant.' },
    ],
  },
});

node({
  id: '5.1', code: '#5.1', title: 'Best next path?', kind: 'decision', lane: 'escalation', since: 1,
  detail: {
    what: 'RMA or field service.',
    owner: 'Escalation owner',
    rules: ['Decided against #5.1G, and the reason recorded — this is the most expensive branch in the flow.'],
  },
});

node({
  id: '5.2', code: '#5.2', title: 'RMA process', kind: 'step', lane: 'rma', since: 1,
  detail: {
    what: 'The unit comes back. Hands over to the existing RMA workflow, which keeps its own stage history.',
    owner: 'RMA owner',
    rules: [
      'The support case links to the RMA rather than duplicating it. Two records of one event is worse than one.',
      'What the support case keeps is the story of how we got here.',
    ],
    iso: [
      { ref: '9001:8.7.1', why: 'Return and correction of nonconforming output.' },
      { ref: '9001:8.5.5', why: 'Post-delivery activity.' },
    ],
  },
});

node({
  id: '5.2A', code: '#5.2A', title: 'RMA inspection / repair / replacement', kind: 'step', lane: 'rma', since: 1,
  detail: {
    what: 'The unit is inspected and the fault is confirmed or not confirmed, then repaired or replaced.',
    owner: 'RMA / service workshop',
    outputs: ['Confirmed physical cause', 'Parts replaced', 'No-fault-found outcome where applicable'],
    rules: [
      'No-fault-found is a result, and an important one — it usually points at documentation, commissioning or expectation rather than hardware.',
      'This is the only step in the whole flow that produces physical evidence. It is the most valuable input to #7.0.',
    ],
    iso: [
      { ref: '9001:8.7.2', why: 'Retain documented information on the nonconformity and the action taken.' },
      { ref: '14001:8.1', why: 'Controlled handling and disposal of replaced parts.' },
    ],
  },
});

node({
  id: '5.2B', code: '#5.2B', title: 'Engineering / quality review needed?', kind: 'decision', lane: 'rma', since: 1,
  detail: {
    what: 'Was this a one-off unit fault, or does it say something about the design, the process or a supplier?',
    owner: 'RMA owner with quality',
    rules: [
      'Yes whenever the cause could recur: a batch, a component, a supplier, a design margin, a build step.',
      'Answering no by default is how a systemic problem stays invisible for a year.',
    ],
    iso: [
      { ref: '9001:10.2.1', why: 'Determine whether similar nonconformities exist or could potentially occur.' },
      { ref: '20000:8.6.3', why: 'Problem management.' },
    ],
  },
});

node({
  id: '5.3', code: '#5.3', title: 'Field service process', kind: 'step', lane: 'rma', since: 1,
  detail: {
    what: 'We go to the unit. Chosen when the unit cannot be released, or the fault is only reproducible in situ.',
    owner: 'Field service owner',
    rules: ['Field visits are the most expensive route and the one with the most operational risk — plan the diagnosis before travelling, not on site.'],
    iso: [
      { ref: '9001:8.5.5', why: 'Post-delivery activities.' },
      { ref: '45001:8.1', why: 'Operational control for work at a customer site, vessel or installation.' },
    ],
  },
});

node({
  id: '5.3A', code: '#5.3A', title: 'Field service execution', kind: 'step', lane: 'rma', since: 1,
  detail: {
    what: 'On-site diagnosis and repair, with findings written back to the case from site.',
    owner: 'Field service engineer',
    outputs: ['On-site findings', 'Installation and environment observations', 'Parts used'],
    rules: [
      'Record the installation context — mounting, cabling, environment, how it is actually used. It is the only chance to see it.',
      'Field observations are the strongest evidence there is for commissioning and manual improvements (#7.1).',
    ],
    iso: [{ ref: '9001:8.5.1', why: 'Controlled conditions for service provision.' }],
  },
});

node({
  id: '5.3B', code: '#5.3B', title: 'Engineering / quality review needed?', kind: 'decision', lane: 'rma', since: 1,
  detail: {
    what: 'Same question as #5.2B, from the field-service side.',
    owner: 'Field service owner with quality',
    rules: ['Field cases disproportionately reveal installation, documentation and commissioning causes rather than component failures.'],
    iso: [{ ref: '9001:10.2.1', why: 'Evaluate the need for action to eliminate the cause.' }],
  },
});

node({
  id: '5.4', code: '#5.4', title: 'Engineering / quality / supplier review', kind: 'step', lane: 'engineering', since: 1,
  detail: {
    what: 'Cross-functional review of a case that has said something about the product rather than about one unit.',
    owner: 'Quality manager, with Product Development and the supplier where relevant',
    rules: [
      'This is line 3 in the tiered model — Product Development. It is a scarce resource and the tier ladder exists to protect it.',
      'Reaching here should be the exception. If it is routine, lines 1 and 2 are missing tools, not effort.',
    ],
    iso: [
      { ref: '9001:10.2', why: 'Nonconformity and corrective action.' },
      { ref: '9001:8.4', why: 'Control of externally provided processes, products and services.' },
    ],
  },
});

node({
  id: '5.5', code: '#5.5', title: 'Link findings to NCR / ECO / supplier case', kind: 'step', lane: 'engineering', since: 1,
  detail: {
    what: 'The finding is converted into a controlled record that has an owner and a due date — an NCR, an ECO, or a supplier complaint.',
    owner: 'Quality manager',
    outputs: ['NCR', 'ECO / design change', 'Supplier complaint', 'Link back to the originating support case'],
    rules: [
      'A finding with no controlled record and no owner will not be actioned. This step is where insight becomes work.',
      'Link both ways, so the next similar case finds the corrective action already in flight.',
    ],
    iso: [
      { ref: '9001:10.2.2', why: 'Retain documented information on the nature of nonconformities and actions taken.' },
      { ref: '9001:8.5.6', why: 'Control of changes.' },
    ],
  },
});

// --- Close states ---------------------------------------------------------

node({
  id: '6.0', code: '#6.0', title: 'Close support case', kind: 'end', lane: 'closed', since: 1,
  detail: {
    what: 'Closed by resolution at line 1 (from #3.3) or line 2 (from #4.4), with the cause and fix documented.',
    owner: 'Case owner',
    rules: ['The cheapest and best close. The share of cases ending here is the primary efficiency measure for the whole flow.'],
    iso: [{ ref: '10002:8.9', why: 'Closing complaints.' }],
  },
});

node({
  id: '6.1', code: '#6.1', title: 'Close as escalated to RMA', kind: 'end', lane: 'closed', since: 1,
  detail: {
    what: 'The support case closes because ownership has moved to the RMA process, which continues under its own workflow.',
    owner: 'Case owner',
    rules: ['Closed, not abandoned — the customer must know where their case has gone and who now owns it.'],
    iso: [{ ref: '10002:8.9', why: 'Closing complaints with a defined outcome.' }],
  },
});

node({
  id: '6.2', code: '#6.2', title: 'Close as escalated to field service', kind: 'end', lane: 'closed', since: 1,
  detail: {
    what: 'Ownership has moved to field service; the support case closes with the handover recorded.',
    owner: 'Case owner',
    iso: [{ ref: '10002:8.9', why: 'Closing complaints.' }],
  },
});

node({
  id: '6.3', code: '#6.3', title: 'Close or link to follow-up process', kind: 'end', lane: 'closed', since: 1,
  detail: {
    what: 'The case closes but the finding lives on in an NCR, ECO or supplier case with its own owner and due date.',
    owner: 'Case owner + quality manager',
    rules: ['The most valuable close in the flow — the one that changes the product rather than just the unit.'],
    iso: [
      { ref: '10002:8.9', why: 'Closing complaints.' },
      { ref: '9001:10.2', why: 'Corrective action continues after the case closes.' },
    ],
  },
});

node({
  id: '6.4', code: '#6.4', title: 'Capture customer satisfaction on close', kind: 'step', lane: 'improve', since: 3, proposed: true,
  detail: {
    what: 'A two-question ask on every close: was the problem solved, and was the handling acceptable.',
    owner: 'Support process owner',
    outputs: ['Resolution satisfaction', 'Process satisfaction', 'Free-text comment'],
    rules: [
      'Two separate questions on purpose — a correct fix delivered badly and a fast reply that did not fix it are different failures.',
      'Keep it to two questions. Longer surveys measure willingness to answer surveys.',
    ],
    why: 'The Rev 02 loop feeds back our own numbers only. Nothing in Rev 01/02 asks the customer whether the outcome was acceptable, which is the one measure ISO 9001 explicitly requires to be monitored.',
    iso: [
      { ref: '9001:9.1.2', why: 'Customer satisfaction shall be monitored, and the methods for it determined.' },
      { ref: '10002:9.3', why: 'Evaluation of satisfaction with the complaints-handling process itself.' },
      { ref: '10004', why: 'Guidance on monitoring and measuring customer satisfaction.' },
    ],
  },
});

node({
  id: '6.5', code: '#6.5', title: 'Installed-base impact check', kind: 'step', lane: 'improve', since: 3, proposed: true,
  detail: {
    what: 'One question asked on every close with a confirmed physical or software cause: which other units could do this?',
    owner: 'Quality manager, using #4.2J batch and firmware correlation',
    outputs: ['Population at risk (batch, lot, firmware, product series)', 'Decision: monitor / proactively notify / recall', 'Feeds #7.0 as a quantified exposure figure'],
    rules: [
      'Answered from data, not intuition — the build records already support the query.',
      'Deciding to do nothing is a valid answer, but it must be a recorded one.',
    ],
    why: 'ISO 9001 10.2 asks whether similar nonconformities exist or could occur. Rev 01/02 stopped at the individual unit — meaning a batch fault could only be discovered by having it reported ten more times.',
    iso: [
      { ref: '9001:10.2.1', why: 'Determine whether similar nonconformities exist or could potentially occur.' },
      { ref: '9001:8.5.2', why: 'Traceability makes the affected population identifiable.' },
    ],
  },
});

// --- Continuous improvement ----------------------------------------------

node({
  id: '7.0', code: '#7.0', title: 'Continuous improvement hub', kind: 'step', lane: 'improve', since: 2,
  detail: {
    what: 'Every close — #6.0, #6.1, #6.2, #6.3 — returns a quantified result here. This is the node that turns support from a cost into an instrument.',
    owner: 'Support process owner with quality manager',
    inputs: ['Quantified result from every closed case', 'Cause classification', 'Tier that resolved it', 'Installed-base exposure from #6.5', 'Customer satisfaction from #6.4'],
    outputs: ['Ranked list of what to fix', 'Input to the < 2/1000 warranty target', 'Input to management review'],
    rules: [
      'From the review: "case closed and documented → feedback on support. Data should be used to feed back such that customers don\'t ask about this again."',
      'Quantified means countable — cause class, tier, time, cost, units exposed. Anecdotes do not aggregate.',
    ],
    iso: [
      { ref: '9001:9.1.3', why: 'Analysis and evaluation of data from monitoring and measurement.' },
      { ref: '9001:10.3', why: 'Continual improvement.' },
      { ref: '10002:9.2', why: 'Analysis and evaluation of complaints to identify systemic and recurring problems.' },
    ],
  },
});

node({
  id: '7.1', code: '#7.1', title: 'Prevention levers — reduce demand', kind: 'step', lane: 'improve', since: 2,
  items: [
    { code: '', title: 'Design', note: 'Remove the failure mode instead of servicing it.' },
    { code: '', title: 'Manuals', note: 'Most "faults" that end no-fault-found are documentation gaps.' },
    { code: '', title: 'Commissioning', note: 'Install it right once and the first-year case load drops.' },
    { code: '', title: 'Partner training', note: 'A trained partner resolves at line 1 instead of forwarding.' },
    { code: '', title: 'M.M — and more', note: 'Read as "m.m." (and more) — the review deliberately left the list open.' },
  ],
  detail: {
    what: 'From the review: "we are obligated to help — but can reduce this by having better design, manuals, commissioning, partner training…". The levers that reduce arriving demand rather than handling it faster.',
    owner: 'Product Development, technical documentation, commissioning and partner management — coordinated by the support process owner',
    rules: [
      'These are the only levers that reduce the amount of help needed. Everything else in the flow makes help cheaper to give.',
      'Each lever needs a named owner and a case-derived justification, or it stays a wish list.',
    ],
    watch: 'The final item is handwritten "M.M" — read as the Norwegian "m.m." (med mer / and more). Confirm.',
    iso: [
      { ref: '9001:10.3', why: 'Continual improvement of the suitability and effectiveness of the QMS.' },
      { ref: '9001:8.3', why: 'Design and development — feeding service findings back into the product.' },
    ],
  },
});

node({
  id: '7.2', code: '#7.2', title: 'Periodic support review → management review', kind: 'step', lane: 'improve', since: 3, proposed: true,
  detail: {
    what: 'A standing review on a fixed cadence that reads the aggregated data and decides what actually changes, then feeds management review.',
    owner: 'Support process owner, chaired with quality manager',
    inputs: ['Trends from #7.0', 'Satisfaction from #6.4', 'Reopen and non-confirmation rate', 'Share resolved per tier'],
    outputs: ['Decisions with owners and dates', 'Management-review input pack', 'Revisions to this flow'],
    rules: [
      'A monthly cadence is a reasonable starting point — quarterly is too slow to catch a batch issue.',
      'Without a decision-making forum, #7.0 is a dashboard nobody acts on.',
    ],
    why: 'Rev 02 created the data sink but not the body that reads it. ISO 9001 requires customer feedback and nonconformity trends to reach management review — this is the mechanism.',
    iso: [
      { ref: '9001:9.3.2 c)', why: 'Management review inputs include trends in customer satisfaction, nonconformities and corrective actions.' },
      { ref: '10002:9.6', why: 'Management review of the complaints-handling process.' },
      { ref: '10002:9.4', why: 'Monitoring of the complaints-handling process.' },
    ],
  },
});

node({
  id: '7.3', code: '#7.3', title: 'Publish known error / FAQ content', kind: 'step', lane: 'improve', since: 3, proposed: true,
  detail: {
    what: 'Turn resolved cases into published content: FAQ entries, service bulletins and assistant answers that stop the next customer needing to ask.',
    owner: 'Support process owner with technical documentation',
    inputs: ['#3.2 first-line solutions', '#4.2L technical responses', 'Recurrence counts from #7.0'],
    outputs: ['FAQ / knowledge-base entries (#3.1F)', 'Known-issue and service-bulletin entries (#3.1G)', 'Assistant answers (#1.6)'],
    rules: [
      'Publish by recurrence: the most-repeated question first. That ordering is available for free from #7.0.',
      'This is the mechanism that closes the loop the review asked for — "such that customers don\'t ask about this again".',
    ],
    why: 'Rev 02 said the data should stop customers asking again but named no mechanism. This is it: the toolkit items #3.1F and #3.1G are consumers of content that nothing in Rev 01/02 produced.',
    iso: [
      { ref: '9001:7.1.6', why: 'Organizational knowledge maintained and made available to the extent necessary.' },
      { ref: '20000:8.6.3', why: 'Problem management — known errors recorded so incidents can be resolved faster.' },
      { ref: '10002:8.1', why: 'Communication — making information about common issues publicly available.' },
    ],
  },
});

// ---------------------------------------------------------------------------
// Diagram layout — ordered bands within phases
// ---------------------------------------------------------------------------

export const PHASES = [
  {
    id: 'p1',
    n: '1',
    title: 'Channels & intake',
    subtitle: 'Ten ways in, one funnel',
    bands: [
      { layout: 'single', node: '1.0' },
      { layout: 'single', node: '1.1' },
      { layout: 'pairGrid', title: 'Channel → what the system captures', pairs: CHANNEL_PAIRS },
      { layout: 'single', node: '2.0', target: true },
      { layout: 'single', node: '2.1' },
      { layout: 'chain', nodes: ['2.1A', '2.1B'], since: 3 },
      { layout: 'sidecar', node: '2.1C', side: 'right', sideLabel: 'YES — SAFETY', sideNodes: ['2.1D'], passLabel: 'NO — CONTINUE', since: 3 },
      { layout: 'sidecar', node: '2.2', side: 'left', sideLabel: 'NO', sideNodes: ['2.3', '2.3A'], passLabel: 'YES', loopLabel: 'RE-CHECK' },
    ],
  },
  {
    id: 'p2',
    n: '2',
    title: 'First responder',
    subtitle: 'Line 1 — Partner / Us',
    from: '#2.2 · YES',
    bands: [
      { layout: 'single', node: '3.0' },
      { layout: 'toolkit', node: '3.1TK' },
      { layout: 'single', node: '3.1' },
      {
        layout: 'branch',
        left: { label: 'NO — ESCALATE', tone: 'warn', nodes: ['4.0', '4.0A', '4.0B'], footer: '▼ continues in phase 3' },
        right: { label: 'YES — SOLVE', tone: 'good', nodes: ['3.2', '3.3'] },
      },
      { layout: 'close', from: 'FROM #3.3', node: '6.0' },
      { layout: 'close', from: 'FROM #3.3 · NOT CONFIRMED', node: '3.3N', since: 3, loopTo: 'to #4.0 — second line' },
    ],
  },
  {
    id: 'p3',
    n: '3',
    title: 'Second line',
    subtitle: 'Line 2 — Us / Sales Support',
    from: '#4.0B',
    bands: [
      { layout: 'single', node: '4.1' },
      { layout: 'toolkit', node: '4.2TK' },
      { layout: 'single', node: '4.2' },
      {
        layout: 'branch',
        left: { label: 'NO — ESCALATE', tone: 'bad', nodes: ['5.0'], footer: '▼ continues in phase 4' },
        right: { label: 'YES — SOLVE', tone: 'good', nodes: ['4.3', '4.4'] },
      },
      { layout: 'close', from: 'FROM #4.4', node: '6.0' },
      { layout: 'close', from: 'FROM #4.4 · NOT CONFIRMED', node: '4.4N', since: 3, loopTo: 'to #5.0 — escalation decision' },
    ],
  },
  {
    id: 'p4',
    n: '4',
    title: 'Escalation, RMA & field service',
    subtitle: 'Line 3 — PD, quality and supplier',
    from: '#5.0',
    bands: [
      { layout: 'toolkit', node: '5.1TK' },
      { layout: 'single', node: '5.1' },
      {
        layout: 'branch',
        left: { label: 'RETURN THE UNIT', tone: 'neutral', nodes: ['5.2', '5.2A', '5.2B'] },
        right: { label: 'GO TO THE UNIT', tone: 'neutral', nodes: ['5.3', '5.3A', '5.3B'] },
      },
      { layout: 'close', from: 'FROM #5.2B · NO', node: '6.1' },
      { layout: 'close', from: 'FROM #5.3B · NO', node: '6.2' },
      { layout: 'chainClose', from: 'FROM #5.2B / #5.3B · YES', nodes: ['5.4', '5.5', '6.3'] },
    ],
  },
  {
    id: 'p5',
    n: '5',
    title: 'Close & improve',
    subtitle: 'What every closed case owes the next one',
    from: '#6.0 – #6.3',
    since: 2,
    bands: [
      { layout: 'chain', nodes: ['6.4', '6.5'], since: 3 },
      { layout: 'hub', nodes: ['7.0', '7.1'], since: 2 },
      { layout: 'chain', nodes: ['7.3', '7.2'], since: 3 },
    ],
  },
];

// ---------------------------------------------------------------------------
// ISO gap analysis — what the standards expect that Rev 01/02 did not do
// ---------------------------------------------------------------------------

export const ISO_GAPS = [
  {
    id: 'g1',
    severity: 'high',
    gap: 'Impact and priority were not assessed until the third tier',
    found: 'Rev 01/02 first looked at customer impact at #5.1B — after line 1 and line 2 had both spent time. Queue order was therefore arrival order.',
    fix: '#2.1B — classify request type, severity, priority and safety at intake.',
    iso: ['10002:8.5', '20000:8.6.1', '20000:8.3.3'],
    nodes: ['2.1B'],
  },
  {
    id: 'g2',
    severity: 'high',
    gap: 'No safety or regulatory interrupt',
    found: 'A potentially unsafe unit followed the same tier ladder as a documentation question.',
    fix: '#2.1C / #2.1D — a hard gate straight to quality manager and PD, in parallel with the normal case.',
    iso: ['10002:8.5', '9001:8.7', '9001:10.2.1'],
    nodes: ['2.1C', '2.1D'],
  },
  {
    id: 'g3',
    severity: 'high',
    gap: 'No path for a customer who does not accept the fix',
    found: '#3.3 and #4.4 both assumed confirmation. A non-confirmation left the process entirely — in practice, back into email.',
    fix: '#3.3N and #4.4N — reopen with the failed attempt recorded, escalating rather than repeating.',
    iso: ['10002:8.8', '9001:10.2.1'],
    nodes: ['3.3N', '4.4N'],
  },
  {
    id: 'g4',
    severity: 'medium',
    gap: 'Receipt was never acknowledged',
    found: 'Case created (#2.1) went straight to screening. The customer had no confirmation and no reference to quote.',
    fix: '#2.1A — automatic acknowledgement with case ID and expected response time.',
    iso: ['10002:8.4', '10002:4.6', '9001:8.2.1 b)'],
    nodes: ['2.1A'],
  },
  {
    id: 'g5',
    severity: 'medium',
    gap: 'Customer satisfaction was not measured',
    found: 'The Rev 02 feedback loop returns our own numbers. Nothing asked the customer whether the outcome was acceptable.',
    fix: '#6.4 — two questions on every close: was it solved, was the handling acceptable.',
    iso: ['9001:9.1.2', '10002:9.3', '10004'],
    nodes: ['6.4'],
  },
  {
    id: 'g6',
    severity: 'medium',
    gap: 'No population question after a confirmed cause',
    found: 'Analysis stopped at the individual unit, so a batch or firmware fault could only surface by being reported repeatedly.',
    fix: '#6.5 — installed-base impact check driven by the #4.2J correlation data.',
    iso: ['9001:10.2.1', '9001:8.5.2'],
    nodes: ['6.5'],
  },
  {
    id: 'g7',
    severity: 'medium',
    gap: 'The information loop had no exit',
    found: '#2.2 → #2.3 → re-check → #2.2 could run forever if the customer never replied.',
    fix: '#2.3A — dormancy close with a stated reason and a live reopen route.',
    iso: ['10002:8.9', '10002:4.15'],
    nodes: ['2.3A'],
  },
  {
    id: 'g8',
    severity: 'medium',
    gap: 'The improvement hub had no reader',
    found: 'Rev 02 routed quantified results to #7.0 but named no forum that reads them or decides anything.',
    fix: '#7.2 — periodic support review with decisions, owners and dates, feeding management review.',
    iso: ['9001:9.3.2 c)', '10002:9.6', '10002:9.4'],
    nodes: ['7.2'],
  },
  {
    id: 'g9',
    severity: 'medium',
    gap: 'Nothing produced the content the toolkits consume',
    found: '#3.1F FAQ and #3.1G known-issue lookup are used by line 1, but no step in Rev 01/02 wrote to them.',
    fix: '#7.3 — publish known-error and FAQ content, ordered by recurrence count.',
    iso: ['9001:7.1.6', '20000:8.6.3', '10002:8.1'],
    nodes: ['7.3'],
  },
  {
    id: 'g10',
    severity: 'low',
    gap: 'No response or resolution targets',
    found: 'The flow defines order of work but no timing, so "responsive" is unmeasurable and cannot be complied with.',
    fix: 'Attach response and resolution targets to the priority set in #2.1B. Not a node — a parameter of the process.',
    iso: ['10002:4.15', '10002:4.6', '20000:8.3.3'],
    nodes: [],
  },
  {
    id: 'g11',
    severity: 'low',
    gap: 'Records and retention not specified',
    found: 'The flow says cases are "documented" but not which records are retained, by whom, or for how long — including personal data in case notes.',
    fix: 'Define the retained record set and its retention period as part of the QMS document set, and state who can see partner and customer cases.',
    iso: ['9001:7.5.3', '10002:4.10'],
    nodes: [],
  },
  {
    id: 'g12',
    severity: 'low',
    gap: 'Competence to act as first or second line is undefined',
    found: 'The toolkits describe what to do but not who is authorised to do it or how that is evidenced.',
    fix: 'Treat the #3.1 and #4.2 toolkits as the training curriculum, and record competence against them.',
    iso: ['9001:7.2', '10002:4.14'],
    nodes: [],
  },
];

export const OPEN_QUESTIONS = [
  {
    id: 'q1',
    question: 'Is the service-line mapping right?',
    detail: 'The review reads "Service Line — 1. Partner/Us · 2. Us/Sales Support · 3. PD" (with something struck through before PD). Read as line 1 / 2 / 3 ownership. Confirm the mapping, what PD covers, and who is on call for line 3.',
  },
  {
    id: 'q2',
    question: 'What is the intake target number?',
    detail: 'The annotation next to #2.0 reads "we want ___% of cases to be done here". Read as 100%. Confirm the number, and whether "here" means inside the system, at intake, or at first line — they imply very different targets.',
  },
  {
    id: 'q3',
    question: 'What does "M.M" mean in the prevention lever list?',
    detail: 'Read as the Norwegian "m.m." (med mer / and more), consistent with the empty dashes drawn under it. Confirm — or name the levers that were intended.',
  },
  {
    id: 'q4',
    question: 'What was marked on #1.2 and #1.5?',
    detail: 'The QR/NFC support page and the support email address are both circled or bracketed on the printed copies without a written comment. Confirm what the mark meant.',
  },
];

// ===========================================================================
// Flow B — RMA intake. A separate controlled document, reached from #5.2 of the
// support loop. Transcribed from the RMA process flowchart; node IDs (A0…Z1)
// are kept from the source so the two can be compared line by line.
// ===========================================================================

export const RMA_DOC = {
  id: 'NI-RMA-FLOW-001',
  title: 'RMA intake — customer issue to closed case',
  revision: 'Rev 01',
  status: 'Draft — for review',
  date: '2026-08-19',
  entryFrom: '#5.2 RMA process (support loop)',
  source: 'RMA process flowchart, August 2026. Node IDs preserved from the source.',
};

const rmaNode = (def) => { N[def.id] = { ...def, flow: 'rma' }; return def; };

/* --- A: how an RMA starts ------------------------------------------------- */

rmaNode({
  id: 'A0', code: 'A0', title: 'Customer experiences issue', kind: 'start', lane: 'customer',
  detail: {
    what: 'Something is wrong with a delivered unit and the customer has decided it needs to come back to us — or at least be looked at.',
    owner: 'Customer',
    rules: ['Most cases should have passed through the support loop first. An RMA opened without that history costs both sides a diagnosis round.'],
  },
});

rmaNode({
  id: 'A1', code: 'A1', title: 'How is the RMA started?', kind: 'decision', lane: 'customer',
  detail: {
    what: 'Three routes in. All three must land on the same device page, because that page is what ties the request to a specific unit.',
    owner: 'Customer',
    rules: ['No route may create an RMA without resolving to a real unit — that is what makes the warranty position and build history available.'],
  },
});

rmaNode({
  id: 'A2', code: 'A2', title: 'Scan the QR code on the device', kind: 'step', lane: 'customer',
  detail: {
    what: 'The customer scans the code physically on the unit and lands directly on that unit\'s device page.',
    owner: 'Customer',
    rules: ['Cheapest route. Zero identification effort, zero transcription error.'],
    outputs: ['Unit resolved with no typing'],
  },
});

rmaNode({
  id: 'A3', code: 'A3', title: 'RMA portal — enter service tag', kind: 'step', lane: 'customer',
  detail: {
    what: 'The customer visits the portal and types the service tag from the label.',
    owner: 'Customer',
    watch: 'Transcription errors on the service tag are the usual failure here. An unmatched tag must offer a clear next step rather than a dead end.',
  },
});

rmaNode({
  id: 'A4', code: 'A4', title: 'Contact Sales — directed to device page', kind: 'step', lane: 'customer',
  detail: {
    what: 'The customer asks whoever they already know at North Invent, and that person points them at the right device page.',
    owner: 'Customer, assisted by whoever was contacted',
    rules: ['The internal person directs the customer to the page rather than starting an RMA by email on their behalf.'],
  },
});

rmaNode({
  id: 'A5', code: 'A5', title: 'Arrive at device page', kind: 'step', lane: 'customer',
  detail: {
    what: 'All three routes converge here. The page already knows the unit, its build history and its warranty position.',
    owner: 'Platform (device page)',
    outputs: ['Service tag and product number', 'Warranty status from scan date', 'Existing case and RMA history for this unit'],
    rules: ['This convergence is what makes one set of RMA numbers possible across three different entry routes.'],
  },
});

/* --- B: submission -------------------------------------------------------- */

rmaNode({
  id: 'B1', code: 'B1', title: 'Customer clicks Start RMA', kind: 'step', lane: 'customer',
  detail: { what: 'The explicit act that begins a return. Everything before this is browsing.', owner: 'Customer' },
});

rmaNode({
  id: 'B2', code: 'B2', title: 'Fill out the RMA form', kind: 'step', lane: 'customer',
  detail: {
    what: 'Name, email, issue category, description, shipment details and an optional image.',
    owner: 'Customer',
    outputs: ['Contact details', 'Issue category and description', 'Shipment details', 'Optional photo evidence'],
    rules: ['The image is optional in the form but decides how fast intake and inspection can work — ask for it clearly rather than burying it.'],
    watch: 'This is the only point where the customer describes the fault in their own words. A thin description here is paid for at inspection.',
  },
});

rmaNode({
  id: 'B3', code: 'B3', title: 'Submit form', kind: 'step', lane: 'customer',
  detail: { what: 'The customer submits. From here the case exists and is ours.', owner: 'Customer' },
});

rmaNode({
  id: 'B4', code: 'B4', title: 'System generates RMA ID', kind: 'step', lane: 'intake',
  detail: {
    what: 'The case gets the identifier both sides quote for the rest of its life.',
    owner: 'Platform (automatic)',
    outputs: ['RMA ID'],
    rules: ['Generated automatically, never assigned by hand — the ID is the join key for shipping, repair, reporting and the closure certificate.'],
  },
});

rmaNode({
  id: 'B5', code: 'B5', title: 'Device page updated, confirmation sent', kind: 'step', lane: 'intake',
  detail: {
    what: 'The unit\'s device page now shows an open RMA, and the customer gets written confirmation.',
    owner: 'Platform (automatic)',
    outputs: ['RMA visible on the device page', 'Confirmation email with the RMA ID'],
    rules: ['Anyone who later scans this unit sees there is an open RMA — that is what stops a second case being opened for the same fault.'],
  },
});

rmaNode({
  id: 'B6', code: 'B6', title: 'Case appears in the NIN dashboard as Pending', kind: 'step', lane: 'rma',
  detail: {
    what: 'The case enters the internal queue. Pending means received but not yet triaged.',
    owner: 'Service / RMA team',
    rules: ['Nothing has been promised to the customer yet beyond receipt. Time spent in Pending is time the customer is waiting without information.'],
  },
});

/* --- C/D: pending, location, shipping in --------------------------------- */

rmaNode({
  id: 'C1', code: 'C1', title: 'Pending stage — internal review', kind: 'step', lane: 'rma',
  detail: {
    what: 'Read the description and evidence, check the unit\'s history, and decide whether a return is actually the right answer.',
    owner: 'Service / RMA team',
    rules: ['A case that should have gone through support first can still be redirected here — cheaper than shipping a working unit across a border.'],
  },
});

rmaNode({
  id: 'C2', code: 'C2', title: 'Assign repair location', kind: 'decision', lane: 'rma',
  detail: {
    what: 'Haugesund or Copenhagen.',
    owner: 'Service / RMA team',
    rules: [
      'Driven by where the unit is, what the repair needs, and where the spares and competence sit.',
      'Getting this wrong costs a second shipment, so it is decided before the customer is given an address.',
    ],
    outputs: ['Assigned repair site'],
  },
});

rmaNode({
  id: 'C3', code: 'C3', title: 'Device page and email updated with address', kind: 'step', lane: 'rma',
  detail: {
    what: 'The customer receives the shipping address and packing instructions, and the same information appears on the device page.',
    owner: 'Service / RMA team + platform',
    outputs: ['Shipping address', 'Packing and labelling instructions', 'RMA ID to mark on the package'],
    rules: ['Both channels carry it: the email is what they act on, the device page is what they find again three weeks later.'],
  },
});

rmaNode({
  id: 'D1', code: 'D1', title: 'Customer ships the device', kind: 'step', lane: 'customer',
  detail: {
    what: 'The unit travels to the assigned site.',
    owner: 'Customer',
    watch: 'Time here is outside our control but inside the customer\'s experience of how long the RMA took. It is deliberately excluded from MTTR — worth being explicit about that when reporting turnaround.',
  },
});

rmaNode({
  id: 'D2', code: 'D2', title: 'NIN confirms arrival', kind: 'step', lane: 'rma', mttr: 'start',
  detail: {
    what: 'The unit is physically received and booked in. This is the moment the MTTR clock starts.',
    owner: 'Receiving site',
    outputs: ['Arrival confirmed', 'MTTR start timestamp'],
    rules: [
      'MTTR measures from arrival, not from when the customer raised the case. Transit time is excluded.',
      'Booking in late inflates our own measured performance — the timestamp must be the real arrival.',
    ],
  },
});

/* --- E/F/G: intake verification ------------------------------------------ */

rmaNode({
  id: 'E1', code: 'E1', title: 'Intake verification', kind: 'step', lane: 'rma',
  detail: {
    what: 'Compare what arrived against what the customer said they were sending.',
    owner: 'Receiving site',
    inputs: ['Declared shipment from B2', 'Physical contents of the package'],
    outputs: ['Match / mismatch determination', 'Intake photos'],
    rules: ['Photograph the unit as received, before anything is opened or touched. It is the only chance to record transit damage as transit damage.'],
  },
});

rmaNode({
  id: 'E2', code: 'E2', title: 'Does the shipment match what was expected?', kind: 'decision', lane: 'rma',
  detail: {
    what: 'Right unit, right accessories, right condition.',
    owner: 'Receiving site',
    rules: ['Checked against the customer\'s own declaration, not against what we assume they meant.'],
  },
});

rmaNode({
  id: 'F2', code: 'F2', title: 'Flag mismatch — may affect warranty', kind: 'step', lane: 'warning',
  detail: {
    what: 'What arrived is not what was declared: a different unit, missing parts, or damage that changes the warranty picture.',
    owner: 'Receiving site',
    outputs: ['Mismatch flag on the case', 'Photo evidence of what arrived', 'Customer informed before work starts'],
    rules: [
      'Raise it with the customer immediately, with photographs. A mismatch discovered at inspection instead of intake looks like an excuse.',
      'A flag is not a denial. It records a fact that the warranty decision at H3 will need.',
    ],
    watch: 'The source flowchart leaves this node with no onward arrow, so it is not stated whether a flagged case still proceeds to repair, waits for the customer, or stops. Confirm the intended behaviour.',
  },
});

rmaNode({
  id: 'F1', code: 'F1', title: 'Move to Awaiting Repair', kind: 'step', lane: 'rma',
  detail: { what: 'Verified and queued. The unit is ours, described, and waiting for a technician.', owner: 'Receiving site' },
});

rmaNode({
  id: 'G1', code: 'G1', title: 'Intake complete', kind: 'step', lane: 'rma',
  detail: {
    what: 'The case now holds everything a technician needs to start without asking anyone a question.',
    owner: 'Receiving site',
    rules: ['Intake being genuinely complete is what lets inspection be about the fault rather than about the paperwork.'],
  },
});

rmaNode({
  id: 'G2', code: 'G2', title: 'NIN clicks Start Repair', kind: 'step', lane: 'rma',
  detail: { what: 'Explicit start. Separating "queued" from "being worked on" is what makes queue time visible.', owner: 'Technician' },
});

/* --- H/I/J: inspection and warranty decision ----------------------------- */

rmaNode({
  id: 'H1', code: 'H1', title: 'Repairing 1/2 — inspection', kind: 'step', lane: 'rma',
  detail: {
    what: 'The unit is opened and examined before any repair is attempted.',
    owner: 'Technician',
    rules: ['Inspection is deliberately its own stage. Diagnosing and repairing in one motion is how a root cause goes unrecorded.'],
  },
});

rmaNode({
  id: 'H2', code: 'H2', title: 'Technician diagnoses the issue', kind: 'step', lane: 'rma',
  detail: {
    what: 'Establish what is actually wrong, and whether it matches what the customer reported.',
    owner: 'Technician',
    outputs: ['Confirmed fault', 'Cause classification', 'Whether the reported symptom is reproduced', 'No-fault-found where applicable'],
    rules: [
      'No-fault-found is a real and important result — it usually points at documentation, commissioning or expectation rather than hardware.',
      'This is the physical evidence the support loop\'s improvement hub cannot get anywhere else.',
    ],
  },
});

rmaNode({
  id: 'H3', code: 'H3', title: 'Warranty decision', kind: 'decision', lane: 'rma',
  detail: {
    what: 'Is this covered, or is it chargeable?',
    owner: 'Service / RMA team, on the technician\'s findings',
    inputs: ['Diagnosis from H2', 'Warranty position from the device page', 'Any mismatch flag from F2'],
    rules: [
      'Decided on the evidence and recorded with reasoning — this is the decision customers challenge, so the reasoning has to survive being read back.',
      'Every case reaching this point counts toward the < 2 per 1000 accepted-warranty target in the support loop.',
    ],
  },
});

rmaNode({
  id: 'I1', code: 'I1', title: 'Accepted — free repair', kind: 'step', lane: 'intake',
  detail: {
    what: 'Covered. No cost to the customer, no decision needed from them.',
    owner: 'Platform (automatic on the decision)',
    rules: ['This is the outcome the whole prevention effort in the support loop exists to reduce the count of.'],
  },
});

rmaNode({
  id: 'I2', code: 'I2', title: 'Denied — create cost quote and repair time', kind: 'step', lane: 'rma',
  detail: {
    what: 'Not covered. Produce a price and a realistic repair duration so the customer has something to decide on.',
    owner: 'Service / RMA team',
    outputs: ['Itemised cost quote', 'Expected repair time', 'Written reason for the denial'],
    rules: [
      'State the reason in plain terms and attach the evidence. A denial without visible reasoning is what turns into a challenge at K4.',
      'Quote the time as well as the money — customers decide on both.',
    ],
  },
});

rmaNode({
  id: 'J1', code: 'J1', title: 'Notify customer — no cost', kind: 'step', lane: 'rma',
  detail: { what: 'Tell the customer it is covered and that no action is needed from them.', owner: 'Service / RMA team' },
});

rmaNode({
  id: 'J2', code: 'J2', title: 'Proceed to execution', kind: 'step', lane: 'rma',
  detail: { what: 'Accepted cases move straight to repair with no wait on the customer.', owner: 'Technician' },
});

/* --- K: the denied path -------------------------------------------------- */

rmaNode({
  id: 'K1', code: 'K1', title: 'Customer decision', kind: 'decision', lane: 'customer',
  detail: {
    what: 'Three ways out: pay for the repair, decline it, or challenge the warranty decision.',
    owner: 'Customer',
    rules: ['The case is now waiting on the customer. That waiting time is theirs, but it still shows in turnaround — worth reporting separately.'],
    watch: 'The source flowchart classes K2 and K4 as internal actions. They read as customer choices; confirm who is recorded as performing them.',
  },
});

rmaNode({
  id: 'K2', code: 'K2', title: 'Accept repair and pay', kind: 'step', lane: 'rma',
  detail: {
    what: 'The customer accepts the quote. Payment is arranged and the repair proceeds.',
    owner: 'Service / RMA team with the customer',
    outputs: ['Accepted quote', 'Payment arrangement'],
  },
});

rmaNode({
  id: 'K3', code: 'K3', title: 'Decline repair — case closed', kind: 'step', lane: 'rma',
  detail: {
    what: 'The customer declines. The unit is returned as-is, or offered into the refurbishment route.',
    owner: 'Service / RMA team',
    outputs: ['Case closed unrepaired', 'Return or refurbishment decision'],
    rules: ['A declined repair still produced a diagnosis. That finding is worth as much to the improvement loop as a completed one.'],
  },
});

rmaNode({
  id: 'K4', code: 'K4', title: 'Challenge the decision', kind: 'step', lane: 'rma',
  detail: {
    what: 'The customer disputes the warranty denial and submits a reason plus supporting files.',
    owner: 'Customer, recorded by the service team',
    outputs: ['Stated grounds for the challenge', 'Supporting files'],
    rules: ['Having a defined route for this is what keeps the argument on the case instead of escalating through sales.'],
  },
});

rmaNode({
  id: 'K5', code: 'K5', title: 'NIN re-evaluates the case', kind: 'step', lane: 'rma',
  detail: {
    what: 'The denial is reviewed against the new information.',
    owner: 'Service / RMA team — escalated beyond whoever made the original call',
    rules: ['Re-evaluation should not be done by the same person who denied it. Otherwise the route exists but changes nothing.'],
  },
});

rmaNode({
  id: 'K6', code: 'K6', title: 'Re-evaluation result', kind: 'decision', lane: 'rma',
  detail: { what: 'Overturn the denial, or uphold it.', owner: 'Service / RMA team' },
});

rmaNode({
  id: 'K7', code: 'K7', title: 'Accepted — cost set to zero', kind: 'step', lane: 'rma',
  detail: {
    what: 'The challenge succeeded. The charge is removed and the case rejoins the main repair line.',
    owner: 'Service / RMA team',
    rules: ['Overturned denials are a signal about how the H3 decision was made. Count them — a rising rate means the criteria or the evidence need work.'],
  },
});

rmaNode({
  id: 'K8', code: 'K8', title: 'Declined — back to customer decision', kind: 'step', lane: 'warning',
  detail: {
    what: 'The denial stands. The customer is back at K1 with pay or decline as the remaining options.',
    owner: 'Service / RMA team',
    rules: ['Explain what was considered and why it did not change the outcome. A bare second refusal is what escalates outside the process.'],
    watch: 'The source flowchart gives this node no owner class. Read as an internal decision; confirm.',
  },
});

/* --- L: execution -------------------------------------------------------- */

rmaNode({
  id: 'L1', code: 'L1', title: 'Repairing 2/2 — execution', kind: 'step', lane: 'rma',
  detail: {
    what: 'The actual repair. Reached from an accepted warranty, a paid quote, or an overturned denial.',
    owner: 'Technician',
    inputs: ['Diagnosis from H2', 'Commercial outcome from H3 or K'],
  },
});

rmaNode({
  id: 'L2', code: 'L2', title: 'Perform repair tasks', kind: 'step', lane: 'rma',
  detail: {
    what: 'SSD, LVDS, PSU and the rest — each as a discrete recorded task.',
    owner: 'Technician',
    rules: ['Per-task recording is what makes component failure countable across the fleet. A single free-text summary cannot be aggregated.'],
    outputs: ['Tasks performed', 'Parts consumed'],
  },
});

rmaNode({
  id: 'L3', code: 'L3', title: 'Add notes for each task', kind: 'step', lane: 'rma',
  detail: {
    what: 'What was found and what was done, task by task.',
    owner: 'Technician',
    rules: ['Written for the next technician who opens this unit, not for the file.'],
  },
});

rmaNode({
  id: 'L4', code: 'L4', title: 'Write repair summary', kind: 'step', lane: 'rma',
  detail: {
    what: 'The customer-facing account of what was wrong and what was done.',
    owner: 'Technician',
    rules: ['This text ends up in the closure certificate at P2, so it is written to be read by the customer.'],
  },
});

rmaNode({
  id: 'L5', code: 'L5', title: 'Finalize repair — Awaiting Shipping', kind: 'step', lane: 'rma',
  detail: {
    what: 'Repair closed out; the case moves to shipping and the customer is asked for a delivery address.',
    owner: 'Technician',
    rules: ['Asking for the return address now rather than at intake matters — units are often bought by one site and returned to another.'],
  },
});

/* --- M/N/O: shipping back and delivery ----------------------------------- */

rmaNode({
  id: 'M1', code: 'M1', title: 'Customer enters shipping address', kind: 'step', lane: 'customer',
  detail: {
    what: 'The customer supplies the delivery address and reads the repair summary.',
    owner: 'Customer',
    rules: ['They see what was done before the unit arrives, which removes most "what did you actually fix?" follow-ups.'],
  },
});

rmaNode({
  id: 'M2', code: 'M2', title: 'NIN books transport and adds tracking', kind: 'step', lane: 'rma',
  detail: {
    what: 'Check the address, book the carrier, attach the tracking reference to the case.',
    owner: 'Service / RMA team',
    outputs: ['Carrier booking', 'Tracking reference on the case'],
  },
});

rmaNode({
  id: 'M3', code: 'M3', title: 'Who covers shipping?', kind: 'decision', lane: 'rma',
  detail: {
    what: 'Follows the warranty outcome — accepted means we pay, denied means the customer pays.',
    owner: 'Service / RMA team',
    rules: ['Determined by H3 and any K7 reversal, so it should never be a fresh argument at this stage.'],
  },
});

rmaNode({
  id: 'M4', code: 'M4', title: 'Warranty accepted — NIN covers cost', kind: 'step', lane: 'rma',
  detail: { what: 'Return shipping is on us.', owner: 'Service / RMA team' },
});

rmaNode({
  id: 'M5', code: 'M5', title: 'Warranty denied — customer covers cost', kind: 'step', lane: 'rma',
  detail: {
    what: 'Return shipping is charged to the customer.',
    owner: 'Service / RMA team',
    rules: ['Flagged in the original quote at I2, not introduced here as a surprise.'],
  },
});

rmaNode({
  id: 'N1', code: 'N1', title: 'Move to Shipped stage', kind: 'step', lane: 'intake',
  detail: { what: 'The unit is on its way back. Both shipping-cost paths converge here.', owner: 'Platform (automatic)' },
});

rmaNode({
  id: 'O1', code: 'O1', title: 'Tracking info sent to customer', kind: 'step', lane: 'customer',
  detail: { what: 'The customer receives the carrier and tracking reference.', owner: 'Platform, to the customer', outputs: ['Tracking reference delivered'] },
});

rmaNode({
  id: 'O2', code: 'O2', title: 'MTTR tracking stops', kind: 'step', lane: 'rma', mttr: 'stop',
  detail: {
    what: 'The measured window closes here — at dispatch, not at delivery.',
    owner: 'Platform (automatic)',
    outputs: ['MTTR stop timestamp', 'Elapsed MTTR for this case'],
    rules: [
      'Stopping at dispatch measures what we control. Return transit is excluded, the same way inbound transit was.',
      'Be explicit about that when quoting MTTR to a customer — their experience of the duration includes both transit legs.',
    ],
  },
});

rmaNode({
  id: 'O3', code: 'O3', title: 'Delivery confirmed — case closed', kind: 'step', lane: 'rma',
  detail: { what: 'Either the customer confirms receipt or we confirm it from the carrier, and the case closes.', owner: 'Service / RMA team or customer' },
});

/* --- P/Z: report, certificate, statistics -------------------------------- */

rmaNode({
  id: 'P1', code: 'P1', title: 'System generates full RMA report', kind: 'step', lane: 'rma',
  detail: {
    what: 'The complete case assembled into one document: fault, diagnosis, warranty decision, tasks, parts and timings.',
    owner: 'Platform, reviewed by the service team',
    outputs: ['Full RMA report'],
  },
});

rmaNode({
  id: 'P2', code: 'P2', title: 'Send certificate of RMA closure', kind: 'step', lane: 'rma',
  detail: {
    what: 'A closure certificate goes to the customer — proof of what was done, useful to them for their own records and audits.',
    owner: 'Service / RMA team',
    rules: ['Built from the L4 summary. Worth getting right: for a maritime customer this document may be shown to a class society or an insurer.'],
  },
});

rmaNode({
  id: 'P3', code: 'P3', title: 'Log data to statistics', kind: 'step', lane: 'rma',
  detail: {
    what: 'MTTR, warranty acceptance rate and fault classification are written to the reporting set.',
    owner: 'Service / RMA team + platform',
    outputs: ['MTTR', 'Warranty accepted / denied', 'Fault class and component', 'Feeds the support loop improvement hub (#7.0)'],
    rules: [
      'This is where the RMA flow hands back to the support loop. Without it, the < 2 per 1000 target has no denominator and no causes.',
      'Fault class is the field that decides whether a batch problem is findable. It is worth the extra thirty seconds.',
    ],
  },
});

rmaNode({
  id: 'Z1', code: 'Z1', title: 'End of RMA process', kind: 'end', lane: 'closed',
  detail: { what: 'Closed, documented, reported, and fed back.', owner: '—' },
});

/* --- annotations: automatic emails --------------------------------------- */

const rmaEmail = (id, at, title, body) => rmaNode({
  id, code: '✉', title, kind: 'annotation', lane: 'email',
  detail: { what: body, owner: 'Platform (automatic)', rules: [`Triggered by ${at}.`] },
});

rmaEmail('EB5', 'B5', 'RMA confirmation', 'Confirms the RMA was received and carries the RMA ID the customer quotes from here on.');
rmaEmail('EC3', 'C3', 'Repair address & instructions', 'The address for the assigned site, plus packing and labelling instructions.');
rmaEmail('EJ1', 'J1', 'Warranty accepted — no cost', 'Tells the customer it is covered and that nothing is needed from them.');
rmaEmail('EL5', 'L5', 'Repair completed — awaiting shipping details', 'Repair is done; asks for the delivery address and shows the repair summary.');
rmaEmail('EO1', 'O1', 'Tracking information', 'Carrier and tracking reference for the return leg.');
rmaEmail('EP2', 'P2', 'RMA closure certificate', 'The closure certificate, with the customer-facing repair summary.');

rmaNode({
  id: 'REF1', code: '◇', title: 'Refurbished programme — planned', kind: 'annotation', lane: 'refurb',
  detail: {
    what: 'A declined repair leaves us holding a diagnosed unit. The plan is to route those into a refurbishment programme rather than returning or scrapping them.',
    owner: 'To be assigned',
    rules: [
      'Marked as the intended home for this in the source flowchart — not built yet.',
      'The app already has a /service/refurbished page, so the landing place exists; what is missing is the decision rule for what qualifies.',
    ],
    watch: 'Needs an owner and an entry rule before it is more than an intention. Until then a declined repair just goes back.',
  },
});

/* --- layout -------------------------------------------------------------- */

export const RMA_PHASES = [
  {
    id: 'r1', n: 'A', title: 'How an RMA starts', subtitle: 'Three routes, one device page',
    bands: [
      { layout: 'single', node: 'A0' },
      { layout: 'single', node: 'A1' },
      { layout: 'fan', cols: [
        { label: 'Scan', tone: 'good', nodes: ['A2'] },
        { label: 'Portal', tone: 'neutral', nodes: ['A3'] },
        { label: 'Via Sales', tone: 'neutral', nodes: ['A4'] },
      ] },
      { layout: 'merge', node: 'A5' },
    ],
  },
  {
    id: 'r2', n: 'B', title: 'Customer submits', subtitle: 'Form to case',
    bands: [
      { layout: 'single', node: 'B1' },
      { layout: 'single', node: 'B2' },
      { layout: 'single', node: 'B3' },
      { layout: 'single', node: 'B4' },
      { layout: 'single', node: 'B5', annotate: 'EB5' },
      { layout: 'single', node: 'B6' },
    ],
  },
  {
    id: 'r3', n: 'C', title: 'Triage, location and intake', subtitle: 'MTTR starts on arrival',
    bands: [
      { layout: 'single', node: 'C1' },
      { layout: 'single', node: 'C2' },
      { layout: 'single', node: 'C3', annotate: 'EC3' },
      { layout: 'single', node: 'D1' },
      { layout: 'single', node: 'D2' },
      { layout: 'single', node: 'E1' },
      { layout: 'sidecar', node: 'E2', side: 'right', sideLabel: 'NO — MISMATCH', sideNodes: ['F2'], passLabel: 'YES — MATCHES' },
      { layout: 'single', node: 'F1' },
      { layout: 'single', node: 'G1' },
      { layout: 'single', node: 'G2' },
    ],
  },
  {
    id: 'r4', n: 'D', title: 'Inspection and the warranty decision', subtitle: 'The decision customers challenge',
    bands: [
      { layout: 'single', node: 'H1' },
      { layout: 'single', node: 'H2' },
      { layout: 'single', node: 'H3' },
      { layout: 'branch',
        left: { label: 'DENIED', tone: 'bad', nodes: ['I2'], footer: '▼ continues in E' },
        right: { label: 'ACCEPTED', tone: 'good', nodes: ['I1', 'J1', 'J2'], annotate: { J1: 'EJ1' }, footer: '▼ continues in F' },
      },
    ],
  },
  {
    id: 'r5', n: 'E', title: 'Denied — what the customer can do', subtitle: 'Pay, decline, or challenge', from: 'I2',
    bands: [
      { layout: 'single', node: 'K1' },
      { layout: 'fan', cols: [
        { label: 'ACCEPT & PAY', tone: 'good', nodes: ['K2'], footer: '▼ to F — execution' },
        { label: 'DECLINE', tone: 'bad', nodes: ['K3', 'REF1'] },
        { label: 'CHALLENGE', tone: 'warn', nodes: ['K4', 'K5', 'K6'] },
      ] },
      { layout: 'branch',
        left: { label: 'CHALLENGE UPHELD', tone: 'good', nodes: ['K7'], footer: '▼ to F — execution' },
        right: { label: 'DENIAL STANDS', tone: 'bad', nodes: ['K8'], footer: '↺ back to K1' },
      },
    ],
  },
  {
    id: 'r6', n: 'F', title: 'Repair execution', subtitle: 'From J2, K2 or K7',
    bands: [
      { layout: 'single', node: 'L1' },
      { layout: 'single', node: 'L2' },
      { layout: 'single', node: 'L3' },
      { layout: 'single', node: 'L4' },
      { layout: 'single', node: 'L5', annotate: 'EL5' },
    ],
  },
  {
    id: 'r7', n: 'G', title: 'Shipping back and delivery', subtitle: 'MTTR stops at dispatch',
    bands: [
      { layout: 'single', node: 'M1' },
      { layout: 'single', node: 'M2' },
      { layout: 'single', node: 'M3' },
      { layout: 'branch',
        left: { label: 'ACCEPTED — WE PAY', tone: 'good', nodes: ['M4'] },
        right: { label: 'DENIED — CUSTOMER PAYS', tone: 'warn', nodes: ['M5'] },
      },
      { layout: 'merge', node: 'N1' },
      { layout: 'single', node: 'O1', annotate: 'EO1' },
      { layout: 'single', node: 'O2' },
      { layout: 'single', node: 'O3' },
    ],
  },
  {
    id: 'r8', n: 'H', title: 'Report, certificate and statistics', subtitle: 'Where the RMA flow feeds the support loop back',
    bands: [
      { layout: 'single', node: 'P1' },
      { layout: 'single', node: 'P2', annotate: 'EP2' },
      { layout: 'single', node: 'P3' },
      { layout: 'single', node: 'Z1' },
    ],
  },
];

export const RMA_OPEN_QUESTIONS = [
  {
    id: 'rq1',
    question: 'What happens to a case flagged as a shipment mismatch (F2)?',
    detail: 'The source flowchart gives F2 no onward arrow. Does a flagged case still proceed to repair, wait for the customer to respond, or stop until the warranty question is settled?',
  },
  {
    id: 'rq2',
    question: 'Who is recorded as performing K2 and K4?',
    detail: 'The source classes "Accept repair and pay" and "Challenge decision" as internal actions, but both read as customer choices. Kept as given; confirm which is intended, because it changes who the flow says is waiting.',
  },
  {
    id: 'rq3',
    question: 'Is MTTR meant to exclude both transit legs?',
    detail: 'As drawn, the clock runs from arrival (D2) to dispatch (O2) — so neither inbound nor return shipping counts. That measures what we control, but it is not what the customer experiences. Confirm, and decide whether to report a second customer-facing turnaround figure alongside it.',
  },
  {
    id: 'rq4',
    question: 'Who owns the refurbished programme, and what qualifies?',
    detail: 'The source marks the declined-repair branch as where a refurbishment programme should go. It needs an owner and an entry rule. The /service/refurbished page already exists as a landing place.',
  },
];

// ===========================================================================
// The two loops, and the interface between them.
//
// Flow A (support loop) runs in Odoo. Flow B (service loop) runs in this RMA
// system. The cut-off at the bottom of the support loop — #5.2 RMA process — is
// where the service loop starts, and the service loop reports back into #7.0.
// ===========================================================================

export const LOOPS = {
  support: {
    key: 'support',
    name: 'Support loop',
    system: 'Odoo Helpdesk',
    doc: 'NI-SUP-FLOW-001',
    scope: 'Everything from the customer asking for help to the case being answered, escalated or handed over.',
  },
  service: {
    key: 'service',
    name: 'Service loop',
    system: 'this RMA system',
    doc: 'NI-RMA-FLOW-001',
    scope: 'Everything from the moment a unit is coming back to the closed, reported and fed-back case.',
  },
};

// Both directions of the interface, as actually implemented.
export const HANDOFF = {
  down: {
    title: 'Support loop → service loop',
    from: '#5.2 RMA process',
    to: 'pending',
    lead: 'The support loop decides a unit has to come back. That decision is the boundary: from here the case is maintained in the RMA system, not in Odoo.',
    steps: [
      'Support reaches #5.1 and the RMA-vs-field-service matrix (#5.1G) says RMA.',
      'An RMA is started against the specific unit — by scanning the tag, by service-tag lookup, or from Support Tools → Start an RMA.',
      '`POST /rma` creates the case at status `pending` and returns the RMA ID.',
      '`odooService.ensureTicket()` opens a matching Odoo Helpdesk ticket and stores its id on the case as `odooTicketId`.',
      'The `created` email goes to the customer via Microsoft Graph, carrying the RMA ID.',
    ],
    note: 'The Odoo ticket and the RMA case are joined by `odooTicketId`. That single field is the whole technical bridge — if it is missing, the two loops are running blind to each other.',
  },
  up: {
    title: 'Service loop → support loop',
    from: 'every status change, then `archived`',
    to: '#7.0 continuous improvement hub',
    lead: 'The service loop reports back continuously, not only at the end. Two channels: the Odoo ticket stays current, and the closed case returns a quantified result.',
    steps: [
      'Every status in `ODOO_NOTIFY_STATUSES` — assigned, received, awaiting_repair, repairing, awaiting_shipping, shipped, archived — posts an internal note onto the Odoo ticket via `postStatusUpdate()`.',
      'Notes are posted as `mail.mt_note`, so support sees the update without the customer being emailed twice — customer email goes out separately over Microsoft Graph.',
      '`PATCH /rma/:id/outcome` records the quantified result: root cause, resolution, parts replaced, failure mode, repeat-case flag and `timeToRepairDays`.',
      'Archiving writes an `ArchivedRma` with the full history and generates the PDF report.',
      '`POST /rma/archived/:id/satisfaction` captures the customer score (0–10) and comment.',
      'That set is what #7.0 ranks prevention work against, and what the < 2 per 1000 warranty target is measured on.',
    ],
    note: 'This is the return leg the printed review asked for — "case closed and documented → feedback on support". It exists in the data model; what is still missing is a forum that reads it (Flow A #7.2).',
  },
};

// ---------------------------------------------------------------------------
// Flow B revisions. Rev 01 is the flowchart as drawn; Rev 02 is the system as
// built. Rev 02 is a rewrite rather than an increment, so each revision has its
// own phase set (RMA_PHASES_BY_REV) instead of per-node `since` gating.
// ---------------------------------------------------------------------------

export const RMA_CURRENT_REVISION = 3;

export const RMA_REVISIONS = [
  {
    rev: 1,
    label: 'Rev 01',
    date: '2026-08-19',
    status: 'Superseded',
    author: 'Flowchart transcription',
    summary: 'The RMA process flowchart as drawn, transcribed node for node with its original IDs.',
    changes: [
      'Fifty-six steps, source IDs A0–Z1 preserved.',
      'Six automatic customer emails.',
      'MTTR drawn from arrival to dispatch.',
      'Refurbishment shown as a planned programme.',
      'One unit per case throughout.',
    ],
  },
  {
    rev: 2,
    label: 'Rev 02',
    date: '2026-08-20',
    status: 'As built',
    author: 'Read from the implementation',
    summary: 'Rewritten from the code so the diagram matches what the RMA system actually does — real statuses, real endpoints, real emails.',
    changes: [
      'Built on the real status machine: pending → assigned → received → awaiting_repair → repairing → awaiting_shipping → shipped → archived, with cancelled / canceled_by_customer / closed_case as recorded exits.',
      '`repairing` is one status with two phases (`repair.phase` = inspection then execution), which is what the drawing showed as Repairing 1/2 and 2/2.',
      'Nine customer emails, not six — created, assigned, received, awaiting_repair, repairing, awaiting_shipping, shipped, archived and warranty_declined, sent over Microsoft Graph and mirrored as internal notes on the Odoo ticket.',
      'MTTR corrected: `computeTimeToRepair` runs from the first `pending`/`assigned` entry to the last `shipped`/`archived` entry, so inbound transit *is* counted and a same-day turnaround records null.',
      'The warranty decision is hard-locked once recorded; only a reviewed challenge (`POST /challenge/review` with outcome `accepted`) may overturn it.',
      'Declining gives two recorded choices — `return_as_is` or `retain_and_close` — not "return or refurbish".',
      'Refurbishment is built, not planned: decide scrap/repair, then tasks, cost and recommission onto a new serial number.',
      'A case can hold several units (#712), each with its own repair state (pending / repaired / scrapped / no_fault); the case cannot leave `repairing` until every unit is finished. Cases can also absorb one another via bundling.',
      'Customer satisfaction (0–10 plus comment) is captured on the archived record — the gap raised against Flow A is already closed on the service side.',
      'Added the Odoo interface explicitly at both ends, so the two loops read as one process.',
    ],
  },
  {
    rev: 3,
    label: 'Rev 03',
    date: '2026-08-20',
    status: 'As built',
    author: 'Measurement pass',
    summary: 'The same process as Rev 02, with the measurement made visible \u2014 where each figure is captured, and whether the process produces it or a person has to.',
    changes: [
      'Added MTBF, absent from every earlier revision: fleet operating hours divided by accepted-warranty failures, from GET /analytics/mtbf, with per-product and per-series breakdowns.',
      'Added the measurement step at the end of the flow \u2014 MTTR, MTBF and the warranty ratio update themselves when the case closes. Nobody compiles them and nobody can forget them.',
      'Confirmed the < 2 per 1000 target from the printed review is already live: WARRANTY_RATIO_TARGET = 0.002, computed by computeWarrantyRatioFull and shown on /current-stats with history, a PDF export and AI insights.',
      'Marked every capture point on the diagram, separating the nine figures the database derives from the two that need a person to classify something.',
      'Recorded a defect: the archive derivation tests for `declined` while the API records `denied`, so a denied case archives as unknown rather than as a non-warranty claim.',
      'Stated both awkward definitions plainly instead of leaving them as questions \u2014 MTBF counts calendar hours since the production scan rather than powered-on hours, and MTTR starts at case creation so inbound shipping is inside it.',
      'Dropped the measurement questions carried in Rev 02: the system already decides them, so they were facts written as questions.',
    ],
  },
];

// ---------------------------------------------------------------------------
// Flow B, Rev 02 — nodes read from the implementation. `api` names the endpoint
// or model field so a reader can go and check the claim.
// ---------------------------------------------------------------------------

const svc = (def) => { N[def.id] = { ...def, flow: 'rma' }; return def; };

/* --- entry ---------------------------------------------------------------- */

svc({
  id: 'v.entry', code: 'entry', title: 'A unit has to come back', kind: 'start', lane: 'customer',
  detail: {
    what: 'The support loop has reached #5.1 and the decision matrix says RMA rather than field service. From here the case belongs to the RMA system.',
    owner: 'Support (in Odoo) hands over; service takes it',
    rules: ['An RMA opened without the support history behind it costs a diagnosis round that has usually already been done.'],
  },
});

svc({
  id: 'v.scan', code: 'route 1', title: 'Scan the QR / NFC tag on the unit', kind: 'step', lane: 'customer',
  detail: {
    what: 'Lands straight on that unit\'s device page with the service tag already resolved.',
    owner: 'Customer',
    rules: ['Cheapest route — no typing, no transcription error.'],
  },
});

svc({
  id: 'v.lookup', code: 'route 2', title: 'Service-tag lookup on the front page', kind: 'step', lane: 'customer',
  detail: {
    what: 'The public front page takes a service tag and opens the device page for it.',
    owner: 'Customer',
    api: 'GET /device/service-tag/:serviceTag — public, rate-limited to one lookup per 10 s per IP',
    watch: 'The rate limit means this route cannot resolve a list of tags. Staff handling several units should use the Start an RMA launcher, which uses the authenticated device search instead.',
  },
});

svc({
  id: 'v.launcher', code: 'route 3', title: 'Support Tools → Start an RMA', kind: 'step', lane: 'first',
  detail: {
    what: 'The staff-side door. Paste one or more service tags, see what each one resolves to and whether it already has an open case, then open the RMA form on the right device page.',
    owner: 'Support / service staff',
    api: 'GET /devices?search=<tag> (authenticated) + GET /rma/:serialNumber',
    rules: [
      'Built for the handover from Odoo: support has the service tag in front of them and needs the case started without hunting for the unit.',
      'Resolves several tags at once, which the public lookup cannot.',
    ],
  },
});

svc({
  id: 'v.device', code: 'device page', title: 'Device page for the unit', kind: 'step', lane: 'intake',
  detail: {
    what: 'All three routes converge here. The page already knows the unit, its build history, its warranty position and any RMA it has had before.',
    owner: 'Platform',
    outputs: ['Service tag and product number', 'Warranty status from scan date', 'Existing and historic RMAs for this unit'],
  },
});

/* --- pending -------------------------------------------------------------- */

svc({
  id: 'v.form', code: 'Start RMA', title: 'Fill in the RMA form', kind: 'step', lane: 'customer',
  detail: {
    what: 'Contact person, company, email, issue type, description, expected return contents, and optional images. Extra units can be added to the same request.',
    owner: 'Customer, or staff on their behalf',
    outputs: ['Contact details', 'issueType (hardware / software / mechanical / packaging / cosmetic / other)', 'Description', 'expectedReturn contents', 'submissionImages'],
    rules: ['The description field prompts for "what was discussed with support" — the form already assumes the support loop came first.'],
  },
});

svc({
  id: 'v.submit', code: 'POST /rma', title: 'Submit — case created at pending', kind: 'step', lane: 'intake',
  detail: {
    what: 'Creates the case, generates the RMA ID, and verifies every submitted service tag against the device registry.',
    owner: 'Platform',
    api: 'POST /rma',
    outputs: ['rmaId (nanoid, 10 chars)', 'status pending + first statusHistory entry', 'devices[] with the primary unit flagged isPrimary'],
    rules: [
      'An unknown service tag is dropped rather than trusted, so a case can never claim a unit that does not exist.',
      'Accepts unauthenticated submissions — the actor is then recorded as "Customer".',
    ],
  },
});

svc({
  id: 'v.odooTicket', code: 'odooTicketId', title: 'Odoo Helpdesk ticket opened', kind: 'step', lane: 'odoo',
  detail: {
    what: 'A matching Odoo Helpdesk ticket is created and its id stored on the case. This is the join between the support loop and the service loop.',
    owner: 'Platform (non-blocking, on submit)',
    api: 'odooService.ensureTicket() → helpdesk.ticket create; id saved to rma.odooTicketId',
    outputs: ['Ticket named "RMA <id> — <serial>"', 'partner_email / partner_name from the case'],
    rules: [
      'Fire-and-forget: a failure here never blocks the submission, it just leaves the loops unlinked.',
      'Requires ODOO_URL / ODOO_DB / ODOO_USER / ODOO_API_KEY to be configured; otherwise the service no-ops.',
    ],
  },
});

svc({
  id: 'v.s_pending', code: 'pending', title: 'Stage — pending', kind: 'stage', lane: 'rma', mttr: 'start',
  detail: {
    what: 'Received, not yet triaged. The MTTR clock starts on this entry.',
    owner: 'Service / RMA team',
    api: 'transitions → assigned, cancelled',
    rules: [
      'MTTR is measured from here, so time spent in pending counts against us — and the unit has not even shipped yet.',
      'Time in pending is time the customer is waiting with nothing but the confirmation email.',
    ],
  },
});

svc({
  id: 'v.bundle', code: 'POST /rma/:id/bundle', title: 'Bundle another case into this one', kind: 'step', lane: 'rma',
  detail: {
    what: 'Absorbs another RMA\'s units into this case so one shipment is handled as one case.',
    owner: 'Service / RMA team',
    api: 'POST /rma/:id/bundle — allowed at pending, assigned, received, awaiting_repair, repairing',
    outputs: ['Units moved onto this case with sourceRmaId kept', 'Absorbed case marked mergedInto / mergedAt / mergedBy'],
    rules: ['The absorbed case keeps existing so old customer links still resolve — it is hidden from the board, not deleted.'],
  },
});

/* --- assigned ------------------------------------------------------------- */

svc({
  id: 'v.assign', code: 'assignedLocation', title: 'Assign the repair site', kind: 'decision', lane: 'rma',
  detail: {
    what: 'Haugesund or Copenhagen. Sets the return address the customer is given.',
    owner: 'Service / RMA team',
    api: 'PUT /rma/:id — status → assigned, assignedLocation set',
    outputs: ['assignedLocation', 'expectedReturn contents confirmed'],
    rules: ['Decided before the customer is given an address — getting it wrong costs a second shipment across a border.'],
  },
});

svc({
  id: 'v.s_assigned', code: 'assigned', title: 'Stage — assigned', kind: 'stage', lane: 'rma',
  detail: {
    what: 'Site chosen, address issued, waiting for the unit to arrive.',
    owner: 'Service / RMA team',
    api: 'transitions → received, cancelled',
  },
});

svc({
  id: 'v.custShipment', code: 'POST /rma/:id/customer-shipment', title: 'Customer declares the shipment', kind: 'step', lane: 'customer',
  detail: {
    what: 'The customer states what they are sending and its tracking number.',
    owner: 'Customer',
    api: 'POST /rma/:id/customer-shipment — only while status is assigned, and only once',
    outputs: ['customerShipment: type, quantity, mounting, accessories, tracking'],
    rules: [
      'Locked to the assigned stage: 422 before, 409 if a tracking number is already recorded.',
      'This declaration is what intake verification compares the arriving package against.',
    ],
  },
});

/* --- received / awaiting_repair ------------------------------------------ */

svc({
  id: 'v.confirmArrival', code: 'POST /rma/:id/received/confirm', title: 'Confirm arrival and verify contents', kind: 'step', lane: 'rma',
  detail: {
    what: 'Book the unit in and compare what arrived against what was declared.',
    owner: 'Receiving site',
    api: 'POST /rma/:id/received/confirm → status received',
    outputs: ['receiptIntake.expected vs .found', 'receiptIntake.status (ok / issue)', 'Intake photos'],
    rules: ['Photograph the unit as received, before anything is opened — the only chance to record transit damage as transit damage.'],
  },
});

svc({
  id: 'v.mismatch', code: 'receiptIntake.mismatch', title: 'Mismatch flagged', kind: 'step', lane: 'warning',
  detail: {
    what: 'What arrived does not match the declaration. Recorded as a flag with notes, alongside the expected and found item lists.',
    owner: 'Receiving site',
    outputs: ['mismatch: true', 'mismatchNotes', 'Both item lists kept for comparison'],
    rules: [
      'A flag, not a denial — it records a fact the warranty decision will need.',
      'The case still proceeds. Unlike the drawing, the implementation does not stop or branch on a mismatch; it annotates.',
    ],
  },
});

svc({
  id: 'v.s_received', code: 'received', title: 'Stage — received', kind: 'stage', lane: 'rma',
  detail: { what: 'Physically here and booked in.', owner: 'Receiving site', api: 'transitions → awaiting_repair, cancelled' },
});

svc({
  id: 'v.s_awaiting_repair', code: 'awaiting_repair', title: 'Stage — awaiting_repair', kind: 'stage', lane: 'rma',
  detail: {
    what: 'Verified and queued for a technician.',
    owner: 'Service / RMA team',
    api: 'transitions → repairing, cancelled',
    rules: ['Splitting this from `repairing` is what makes queue time visible separately from work time.'],
  },
});

/* --- repairing: inspection ---------------------------------------------- */

svc({
  id: 'v.s_repairing', code: 'repairing', title: 'Stage — repairing', kind: 'stage', lane: 'rma',
  detail: {
    what: 'One status covering both phases of the work. `repair.phase` moves from inspection to execution inside it, which is what the drawing showed as Repairing 1/2 and 2/2.',
    owner: 'Technician',
    api: 'transitions → awaiting_shipping, canceled_by_customer, closed_case, cancelled',
    rules: [
      'The case also waits here while a chargeable quote is with the customer — a case in `repairing` is not necessarily being worked on.',
      'It cannot leave until every unit on the case has a final repair state.',
    ],
  },
});

svc({
  id: 'v.startRepair', code: 'POST /rma/:id/repair/start', title: 'Start repair — enters inspection', kind: 'step', lane: 'rma',
  detail: {
    what: 'Moves the case to `repairing` with `repair.phase = inspection`.',
    owner: 'Technician',
    api: 'POST /rma/:id/repair/start',
    outputs: ['status repairing', 'repair.phase inspection', 'repair.startedAt / startedBy'],
  },
});

svc({
  id: 'v.inspection', code: 'POST /rma/:id/repair/inspection', title: 'Inspect and record findings', kind: 'step', lane: 'rma',
  detail: {
    what: 'Examine the unit and record what is actually wrong, per unit on a multi-unit case.',
    owner: 'Technician',
    api: 'POST /rma/:id/repair/inspection',
    outputs: ['Findings and defect classification', 'Whether the reported symptom reproduces'],
    rules: ['Inspection is its own phase on purpose. Diagnosing and repairing in one motion is how a root cause goes unrecorded.'],
  },
});

svc({
  id: 'v.warranty', code: 'POST /rma/:id/repair/warranty', title: 'Warranty decision — accepted or denied', kind: 'decision', lane: 'rma',
  detail: {
    what: 'The commercial decision, recorded with a note and locked.',
    owner: 'Service / RMA team on the technician\'s findings',
    api: 'POST /rma/:id/repair/warranty — accepted | denied',
    outputs: ['repair.warranty.decision / decidedAt / decidedBy / note', 'statusHistory entry "Warranty decision recorded"'],
    rules: [
      'Hard-locked once recorded: a second attempt returns 409 WARRANTY_LOCKED. There is no edit and no reversal.',
      'The only sanctioned override is a challenge review that comes back `accepted`.',
      'A denial fires the warranty_declined email immediately.',
    ],
    watch: 'Because it is locked, the note matters — it is the reasoning the customer will be shown if they challenge.',
  },
});

svc({
  id: 'v.finalizeInspection', code: 'POST /rma/:id/repair/inspection/finalize', title: 'Finalize inspection', kind: 'step', lane: 'rma',
  detail: {
    what: 'Closes the inspection phase and moves `repair.phase` to execution.',
    owner: 'Technician',
    api: 'POST /rma/:id/repair/inspection/finalize',
    outputs: ['repair.inspectionFinalizedAt / By', 'repair.phase execution'],
  },
});

/* --- the denied path ---------------------------------------------------- */

svc({
  id: 'v.quote', code: 'repair.quote', title: 'Cost quote and repair time', kind: 'step', lane: 'rma',
  detail: {
    what: 'Price, currency, expected timing and parts availability, so the customer has something concrete to decide on.',
    owner: 'Service / RMA team',
    outputs: ['repair.quote (amount, currency)', 'repair.timing', 'parts in-stock / wait days / ETA'],
    rules: ['Quote the time as well as the money — customers decide on both.'],
  },
});

svc({
  id: 'v.approval', code: 'repair.customerApproval', title: 'Waiting on the customer', kind: 'decision', lane: 'customer',
  detail: {
    what: 'Approval state on the case: pending, accepted, declined or challenged.',
    owner: 'Customer',
    api: 'POST /rma/:id/customer-decision',
    outputs: ['status pending | accepted | declined | challenged', 'decidedAt / decidedBy / note'],
    rules: ['`required` is set when a denial produced a chargeable quote. The case sits in `repairing` while it waits.'],
  },
});

svc({
  id: 'v.payment', code: 'POST /rma/:id/repair/payment/confirm', title: 'Payment confirmed', kind: 'step', lane: 'rma',
  detail: { what: 'Records that the chargeable repair has been paid for, releasing it into execution.', owner: 'Service / RMA team', api: 'POST /rma/:id/repair/payment/confirm', outputs: ['repair.payment'] },
});

svc({
  id: 'v.challenge', code: 'POST /rma/:id/challenge', title: 'Customer challenges the denial', kind: 'step', lane: 'customer',
  detail: {
    what: 'The customer disputes the warranty denial with a written reason and supporting files. Challenges are numbered in rounds.',
    owner: 'Customer, recorded on the case',
    api: 'POST /rma/:id/challenge (multipart — reason + files)',
    outputs: ['repair.challenges[] entry for the round', 'repair.challengeRound incremented', 'Uploaded files on the case'],
    rules: ['A defined route keeps the argument on the case instead of escalating through sales.'],
  },
});

svc({
  id: 'v.challengeReview', code: 'POST /rma/:id/challenge/review', title: 'Challenge reviewed', kind: 'decision', lane: 'rma',
  detail: {
    what: 'Staff review the round and return accepted or declined. This is the one path that can overturn the locked warranty decision.',
    owner: 'Service / RMA team — should not be the person who made the original call',
    api: 'POST /rma/:id/challenge/review — outcome accepted | declined',
    outputs: [
      'accepted → repair.warranty.decision set to accepted, customerApproval { required: false, status: accepted }',
      'declined → approval returns to required, the customer is back at the decision',
      'Review recorded against the round with a staff note',
    ],
    rules: [
      'A round can only be reviewed once — a second, different outcome returns CHALLENGE_ALREADY_REVIEWED.',
      'Overturned denials are a signal about how the warranty decision was made. A rising rate means the criteria or the evidence need work.',
    ],
  },
});

svc({
  id: 'v.declineReturn', code: 'decline-path/return', title: 'Decline — return the unit as-is', kind: 'step', lane: 'rma',
  detail: {
    what: 'The customer declines the chargeable repair and wants the unit back unrepaired.',
    owner: 'Service / RMA team',
    api: 'POST /rma/:id/decline-path/return → postDecline.choice = return_as_is',
    outputs: ['postDecline.choice return_as_is', 'shippingCostAck — the customer acknowledges return shipping is theirs'],
  },
});

svc({
  id: 'v.declineClose', code: 'decline-path/close', title: 'Decline — we retain the unit', kind: 'step', lane: 'rma',
  detail: {
    what: 'The customer declines and leaves the unit with us. This is the door into refurbishment.',
    owner: 'Service / RMA team',
    api: 'POST /rma/:id/decline-path/close → postDecline.choice = retain_and_close',
    outputs: ['postDecline.choice retain_and_close', 'Case eligible for the refurbishment track'],
  },
});

/* --- refurbishment (built) --------------------------------------------- */

svc({
  id: 'v.refurb', code: 'refurbishment', title: 'Refurbishment — scrap or repair', kind: 'decision', lane: 'refurb',
  detail: {
    what: 'A retained unit is assessed and either scrapped or repaired for stock. Not a plan — this is implemented.',
    owner: 'Service / RMA team',
    api: 'POST /rma/:id/refurbishment/decide — decision scrap | repair',
    outputs: ['refurbishment.status pending → scrapped | repairing → recommissioned', 'refurbId'],
    rules: ['The drawing marked this branch as "implement refurbished program here". It exists: decide, then tasks, cost and recommission.'],
  },
});

svc({
  id: 'v.refurbWork', code: 'refurbishment/task · /cost', title: 'Refurbishment work and cost', kind: 'step', lane: 'refurb',
  detail: {
    what: 'Per-task progress and the cost of bringing the unit back to sellable condition.',
    owner: 'Technician',
    api: 'PUT /rma/:id/refurbishment/task · PUT /rma/:id/refurbishment/cost',
    outputs: ['refurbishment.repairTasks[]', 'repairCost / repairCurrency', 'repairStartedAt / repairCompletedAt'],
  },
});

svc({
  id: 'v.recommission', code: 'refurbishment/recommission', title: 'Recommission onto a new serial', kind: 'end', lane: 'refurb',
  detail: {
    what: 'The refurbished unit re-enters stock under a new serial number, linked back to the case it came from.',
    owner: 'Service / RMA team',
    api: 'PUT /rma/:id/refurbishment/recommission',
    outputs: ['newSerialNumber / newDeviceId', 'recommissionedAt / By / Notes'],
    rules: ['The link back to the originating RMA is what keeps a refurbished unit\'s history honest.'],
  },
});

/* --- repairing: execution ---------------------------------------------- */

svc({
  id: 'v.tasks', code: 'POST /rma/:id/repair/tasks', title: 'Record repair tasks', kind: 'step', lane: 'rma',
  detail: {
    what: 'Each task recorded discretely, with notes and due/completed dates.',
    owner: 'Technician',
    api: 'POST /rma/:id/repair/tasks',
    outputs: ['repair.tasks[]'],
    rules: ['Per-task recording is what makes component failure countable across the fleet. One free-text summary cannot be aggregated.'],
  },
});

svc({
  id: 'v.deviceStates', code: 'POST /rma/:id/devices/repair', title: 'Per-unit outcome', kind: 'step', lane: 'rma',
  detail: {
    what: 'Each unit on the case is set to repaired, scrapped or no_fault, with a note and fault categories.',
    owner: 'Technician',
    api: 'POST /rma/:id/devices/repair',
    outputs: ['devices[].repair.state — pending | repaired | scrapped | no_fault', 'completedAt / By / note / categories'],
    rules: [
      'The case cannot leave `repairing` until every unit is finished. That is enforced, not a convention.',
      '`no_fault` is a first-class outcome and an important one — it usually points at documentation, commissioning or expectation rather than hardware.',
    ],
  },
});

svc({
  id: 'v.summary', code: 'repair.summary', title: 'Write the repair summary', kind: 'step', lane: 'rma',
  detail: {
    what: 'The customer-facing account of what was wrong and what was done.',
    owner: 'Technician',
    rules: ['This text goes into the archived PDF report, so it is written to be read by the customer.'],
  },
});

svc({
  id: 'v.finalizeRepair', code: 'POST /rma/:id/repair/finalize', title: 'Finalize repair', kind: 'step', lane: 'rma',
  detail: {
    what: 'Closes the repair and moves the case to awaiting_shipping.',
    owner: 'Technician',
    api: 'POST /rma/:id/repair/finalize → status awaiting_shipping',
    outputs: ['repair.finalizedAt / By', 'status awaiting_shipping'],
  },
});

/* --- awaiting_shipping / shipped -------------------------------------- */

svc({
  id: 'v.s_awaiting_shipping', code: 'awaiting_shipping', title: 'Stage — awaiting_shipping', kind: 'stage', lane: 'rma',
  detail: {
    what: 'Repaired, waiting on the return address and a carrier booking.',
    owner: 'Service / RMA team',
    api: 'transitions → shipped, closed_case, cancelled',
  },
});

svc({
  id: 'v.shipDetails', code: 'shipping.details', title: 'Customer gives the return address', kind: 'step', lane: 'customer',
  detail: {
    what: 'Recipient, street, city, region, postal code and country — plus the customer reads the repair summary.',
    owner: 'Customer',
    outputs: ['shipping.details'],
    rules: ['Asked now rather than at intake, because units are often bought by one site and returned to another.'],
  },
});

svc({
  id: 'v.dispatch', code: 'shipping.dispatch', title: 'Book carrier, add tracking, set payer', kind: 'step', lane: 'rma',
  detail: {
    what: 'Carrier booking and tracking reference on the case, and who pays for the return leg.',
    owner: 'Service / RMA team',
    outputs: ['shipping.dispatch', 'shipping.payer'],
    rules: ['Payer follows the warranty outcome and any challenge reversal — it should never be a fresh argument at this point.'],
  },
});

svc({
  id: 'v.s_shipped', code: 'shipped', title: 'Stage — shipped', kind: 'stage', lane: 'rma', mttr: 'stop',
  detail: {
    what: 'Dispatched. The MTTR clock stops on this entry (or on archived, whichever is recorded last).',
    owner: 'Service / RMA team',
    api: 'transitions → archived',
    rules: ['Only `archived` follows `shipped` — there is no route back, so anything unresolved has to be handled before dispatch.'],
  },
});

svc({
  id: 'v.deliver', code: 'shipping.deliveryConfirmedAt', title: 'Delivery confirmed', kind: 'step', lane: 'rma',
  detail: {
    what: 'Either the customer confirms receipt or we confirm it from the carrier; the source is recorded.',
    owner: 'Customer or service team',
    outputs: ['deliveryConfirmedAt / By', 'deliveryConfirmationSource'],
  },
});

/* --- close, report, feed back ----------------------------------------- */

svc({
  id: 'v.outcome', code: 'PATCH /rma/:id/outcome', title: 'Record the quantified outcome', kind: 'step', lane: 'improve',
  detail: {
    what: 'The learning data: root cause, resolution type, parts replaced, failure mode, repeat-case flag, technician hours and time to repair.',
    owner: 'Service / RMA team',
    api: 'PATCH /rma/:id/outcome',
    outputs: ['outcome.rootCause / resolution / partsReplaced / failureMode / repeatCase', 'outcome.timeToRepairDays (computed)', 'technicianTimeHours / componentAgeMonths'],
    rules: [
      'This is the field set the support loop\'s improvement hub ranks prevention work against. Skipping it makes the case invisible to #7.0.',
      'Root cause is what decides whether a batch problem is findable later.',
    ],
  },
});

svc({
  id: 'v.archive', code: 'PUT /rma/archive/:id', title: 'Archive — report and certificate', kind: 'step', lane: 'rma',
  detail: {
    what: 'Writes an ArchivedRma with the full history, generates the PDF report and issues a report token so the customer can retrieve it.',
    owner: 'Service / RMA team',
    api: 'PUT /rma/archive/:id → ArchivedRma + generateArchivedRmaPdf + reportToken',
    outputs: ['ArchivedRma record', 'PDF report (GET /rma-archive/pdf/:id)', 'archived email to the customer'],
    rules: ['For a maritime customer this document may be shown to a class society or an insurer, so the summary written at execution matters here.'],
  },
});

svc({
  id: 'v.satisfaction', code: 'POST /rma/archived/:id/satisfaction', title: 'Customer satisfaction score', kind: 'step', lane: 'improve',
  detail: {
    what: 'A 0–10 score plus an optional comment, stored on the archived record.',
    owner: 'Customer',
    api: 'POST /rma/archived/:id/satisfaction',
    outputs: ['satisfaction.score (0–10)', 'satisfaction.comment', 'submittedAt'],
    rules: ['This is the only measure in either loop that reflects the customer rather than us. Flow A raises it as a gap — on the service side it is already built.'],
  },
});

svc({
  id: 'v.s_archived', code: 'archived', title: 'Stage — archived', kind: 'end', lane: 'closed',
  detail: { what: 'Terminal. The case lives on as an ArchivedRma with its report.', owner: '—', api: 'transitions → (none)' },
});

svc({
  id: 'v.metrics', code: 'metrics', title: 'MTTR, MTBF and the warranty ratio update themselves', kind: 'step', lane: 'metric',
  detail: {
    what: 'Nobody compiles these. Closing the case is what moves them — the figures are read straight off the database whenever a dashboard asks.',
    owner: 'Nobody — this is the point',
    outputs: [
      'MTTR — from statusHistory, stored on the case as outcome.timeToRepairDays',
      'MTBF — fleet operating hours ÷ accepted-warranty failures, recomputed on request',
      'Warranty ratio — accepted claims ÷ devices currently within warranty, against the 0.002 target',
      'Device age at failure, prior-RMA count and the warranty binary, all derived at archive',
    ],
    api: 'GET /analytics/mtbf · GET /analytics/warranty-ratio · GET /product-families/analytics · outcome.timeToRepairDays',
    rules: [
      'The derived figures cannot be forgotten. The classification fields on the previous step can, and that is the only real gap.',
      'The < 2 per 1000 target from the printed review is already live: WARRANTY_RATIO_TARGET = 0.002 on /current-stats.',
    ],
    watch: 'MTBF counts calendar hours since the production scan, not powered-on hours, and MTTR includes the inbound shipping from the customer. Both are fine as trends; state the definition if either is quoted outside.',
  },
});

svc({
  id: 'v.feedback', code: '→ #7.0', title: 'Quantified result returns to the support loop', kind: 'step', lane: 'improve',
  detail: {
    what: 'Outcome, satisfaction and MTTR land in the reporting set that Flow A\'s improvement hub reads.',
    owner: 'Support process owner with quality',
    rules: ['This is the return leg the printed review asked for. The data exists; Flow A #7.2 is the forum that still has to read it.'],
  },
});

/* --- recorded exits --------------------------------------------------- */

svc({
  id: 'v.cancel', code: 'POST /rma/:id/cancel', title: 'Close the case prematurely', kind: 'step', lane: 'warning',
  detail: {
    what: 'A discouraged control that ends a case before it completes, with a required reason and category.',
    owner: 'Service / RMA team',
    api: 'POST /rma/:id/cancel — allowed from pending, assigned, received, awaiting_repair, repairing, awaiting_shipping',
    outputs: ['status cancelled', 'cancellation.at / by / category / reason / notes / stage'],
    rules: [
      'The stage it was closed at is recorded, so premature closures are queryable alongside completed cases.',
      'Captured as learning data tagged type: premature_closure — a closure is a data point, not a deletion.',
    ],
  },
});

svc({
  id: 'v.customerCancel', code: 'canceled_by_customer', title: 'Cancelled by the customer', kind: 'end', lane: 'closed',
  detail: {
    what: 'The customer pulls out during repair.',
    owner: 'Service / RMA team',
    api: 'reachable from repairing',
  },
});

svc({
  id: 'v.closedCase', code: 'closed_case', title: 'Closed without dispatch', kind: 'end', lane: 'closed',
  detail: {
    what: 'The case ends without the unit going back — typically the retain-and-close route into refurbishment.',
    owner: 'Service / RMA team',
    api: 'reachable from repairing and awaiting_shipping',
  },
});

/* --- emails (nine, over Microsoft Graph) ------------------------------ */

const svcEmail = (id, status, title, body) => svc({
  id, code: '✉', title, kind: 'annotation', lane: 'email',
  detail: {
    what: body,
    owner: 'Platform (automatic)',
    api: `template "${status}" — msGraphService.sendMail(), mirrored onto the Odoo ticket as an internal note`,
  },
});

svcEmail('ve.created', 'created', 'created', 'Confirms the RMA and carries the RMA ID the customer quotes from here on.');
svcEmail('ve.assigned', 'assigned', 'assigned', 'The return address for the assigned site, plus packing and labelling instructions.');
svcEmail('ve.received', 'received', 'received', 'Confirms the unit arrived and is booked in.');
svcEmail('ve.awaitingRepair', 'awaiting_repair', 'awaiting_repair', 'Verified and queued for a technician.');
svcEmail('ve.repairing', 'repairing', 'repairing', 'Work has started.');
svcEmail('ve.declined', 'warranty_declined', 'warranty_declined', 'The denial with its reasoning, the quote, and the routes open to the customer — accept, decline or challenge.');
svcEmail('ve.awaitingShipping', 'awaiting_shipping', 'awaiting_shipping', 'Repair complete; asks for the return address and shows the repair summary.');
svcEmail('ve.shipped', 'shipped', 'shipped', 'Carrier and tracking reference for the return leg.');
svcEmail('ve.archived', 'archived', 'archived', 'Case closed, with the PDF report available through the report token.');

/* --- Flow B Rev 02 layout --------------------------------------------- */

const RMA_PHASES_REV2 = [
  {
    id: 'v1', n: 'A', title: 'Starting an RMA', subtitle: 'Three doors, one device page', from: '#5.2 · support loop',
    bands: [
      { layout: 'single', node: 'v.entry' },
      { layout: 'fan', cols: [
        { label: 'Scan the tag', tone: 'good', nodes: ['v.scan'] },
        { label: 'Front-page lookup', tone: 'neutral', nodes: ['v.lookup'] },
        { label: 'Staff launcher', tone: 'warn', nodes: ['v.launcher'] },
      ] },
      { layout: 'merge', node: 'v.device' },
      { layout: 'single', node: 'v.form' },
      { layout: 'single', node: 'v.submit' },
      { layout: 'single', node: 'v.odooTicket', annotate: 've.created' },
      { layout: 'sidecar', node: 'v.s_pending', side: 'right', sideLabel: 'MULTI-UNIT', sideNodes: ['v.bundle'], passLabel: 'ASSIGN A SITE' },
    ],
  },
  {
    id: 'v2', n: 'B', title: 'Assigned — getting the unit here', subtitle: 'Haugesund or Copenhagen',
    bands: [
      { layout: 'single', node: 'v.assign' },
      { layout: 'single', node: 'v.s_assigned', annotate: 've.assigned' },
      { layout: 'single', node: 'v.custShipment' },
    ],
  },
  {
    id: 'v3', n: 'C', title: 'Arrival and intake verification', subtitle: 'received → awaiting_repair',
    bands: [
      { layout: 'sidecar', node: 'v.confirmArrival', side: 'right', sideLabel: 'DOES NOT MATCH', sideNodes: ['v.mismatch'], passLabel: 'MATCHES' },
      { layout: 'single', node: 'v.s_received', annotate: 've.received' },
      { layout: 'single', node: 'v.s_awaiting_repair', annotate: 've.awaitingRepair' },
    ],
  },
  {
    id: 'v4', n: 'D', title: 'Repairing, phase 1 — inspection', subtitle: 'One status, two phases',
    bands: [
      { layout: 'single', node: 'v.startRepair', annotate: 've.repairing' },
      { layout: 'single', node: 'v.s_repairing' },
      { layout: 'single', node: 'v.inspection' },
      { layout: 'single', node: 'v.warranty' },
      { layout: 'branch',
        left: { label: 'DENIED — CHARGEABLE', tone: 'bad', nodes: ['v.quote'], annotate: { 'v.quote': 've.declined' }, footer: '▼ continues in E' },
        right: { label: 'ACCEPTED — COVERED', tone: 'good', nodes: ['v.finalizeInspection'], footer: '▼ continues in F' },
      },
    ],
  },
  {
    id: 'v5', n: 'E', title: 'Denied — the customer decides', subtitle: 'Accept, decline, or challenge', from: 'warranty denied',
    bands: [
      { layout: 'single', node: 'v.approval' },
      { layout: 'fan', cols: [
        { label: 'ACCEPT', tone: 'good', nodes: ['v.payment'], footer: '▼ to F — execution' },
        { label: 'DECLINE', tone: 'bad', nodes: ['v.declineReturn'] },
        { label: 'CHALLENGE', tone: 'warn', nodes: ['v.challenge', 'v.challengeReview'] },
      ] },
      { layout: 'branch',
        left: { label: 'CHALLENGE ACCEPTED', tone: 'good', nodes: ['v.finalizeInspection'], footer: 'warranty unlocked → F' },
        right: { label: 'CHALLENGE DECLINED', tone: 'bad', nodes: ['v.declineClose'], footer: '↺ or back to the decision' },
      },
      { layout: 'chainClose', from: 'RETAINED UNIT', nodes: ['v.refurb', 'v.refurbWork', 'v.recommission'] },
    ],
  },
  {
    id: 'v6', n: 'F', title: 'Repairing, phase 2 — execution', subtitle: 'Per unit, and the case waits for all of them',
    bands: [
      { layout: 'single', node: 'v.tasks' },
      { layout: 'single', node: 'v.deviceStates' },
      { layout: 'single', node: 'v.summary' },
      { layout: 'single', node: 'v.finalizeRepair' },
    ],
  },
  {
    id: 'v7', n: 'G', title: 'Back to the customer', subtitle: 'awaiting_shipping → shipped',
    bands: [
      { layout: 'single', node: 'v.s_awaiting_shipping', annotate: 've.awaitingShipping' },
      { layout: 'single', node: 'v.shipDetails' },
      { layout: 'single', node: 'v.dispatch' },
      { layout: 'single', node: 'v.s_shipped', annotate: 've.shipped' },
      { layout: 'single', node: 'v.deliver' },
    ],
  },
  {
    id: 'v8', n: 'H', title: 'Close, report, feed back', subtitle: 'Where the service loop answers to the support loop',
    bands: [
      { layout: 'single', node: 'v.outcome' },
      { layout: 'single', node: 'v.archive', annotate: 've.archived' },
      { layout: 'single', node: 'v.satisfaction' },
      { layout: 'single', node: 'v.s_archived' },
      { layout: 'single', node: 'v.feedback' },
    ],
  },
  {
    id: 'v9', n: 'X', title: 'Recorded exits', subtitle: 'Cases that end without a dispatch — still counted',
    bands: [
      { layout: 'fan', cols: [
        { label: 'STAFF CLOSES EARLY', tone: 'warn', nodes: ['v.cancel'] },
        { label: 'CUSTOMER PULLS OUT', tone: 'bad', nodes: ['v.customerCancel'] },
        { label: 'CLOSED, NOT SHIPPED', tone: 'neutral', nodes: ['v.closedCase'] },
      ] },
    ],
  },
];

// Rev 03 is Rev 02 with measurement made explicit — derived from it so the two
// cannot drift apart by accident.
const RMA_PHASES_REV3 = RMA_PHASES_REV2.map(phase => {
  if (phase.id !== 'v8') return phase;
  const bands = [];
  phase.bands.forEach(band => {
    if (band.node === 'v.feedback') bands.push({ layout: 'single', node: 'v.metrics' });
    bands.push(band);
  });
  return { ...phase, subtitle: 'Every figure below is produced by closing the case', bands };
});

export const RMA_PHASES_BY_REV = {
  1: RMA_PHASES,
  2: RMA_PHASES_REV2,
  3: RMA_PHASES_REV3,
};

// Where the drawing and the implementation disagree. Every row was read from the
// code, not inferred — the `where` column names the file to check.
export const RMA_DRIFT = [
  {
    id: 'd1', severity: 'high',
    topic: 'The MTTR window',
    drawn: 'Starts when the unit arrives (D2), stops at dispatch (O2). Neither transit leg counts.',
    built: '`computeTimeToRepair` takes the first `pending` **or** `assigned` history entry and the last `shipped` **or** `archived` entry, in whole days. So the clock starts at case creation — inbound transit *is* counted — and a same-day turnaround records `null` rather than 0.',
    where: 'backend/routes/rma.js — computeTimeToRepair',
    soWhat: 'Quoted MTTR is larger than the drawing implies, and includes time we do not control. Decide which definition you want to report before publishing a number.',
  },
  {
    id: 'd2', severity: 'high',
    topic: 'The warranty decision is locked',
    drawn: 'A successful challenge "sets cost to zero", implying the decision can be edited.',
    built: '`POST /repair/warranty` refuses a second decision with 409 WARRANTY_LOCKED. The only sanctioned override is `POST /challenge/review` with outcome `accepted`, which sets the decision to accepted and clears the approval requirement.',
    where: 'backend/routes/rma.js — repair/warranty, challenge/review',
    soWhat: 'The reasoning note written at the decision is what the customer sees if they challenge, and it cannot be quietly revised later.',
  },
  {
    id: 'd3', severity: 'medium',
    topic: 'A mismatch does not branch',
    drawn: 'A shipment mismatch (F2) is a separate outcome of the verification decision, with no onward arrow.',
    built: 'Intake records `receiptIntake.mismatch` plus notes and both item lists, and the case proceeds normally. There is no separate stage and nothing blocks.',
    where: 'backend/models/RMA.js — receiptIntakeSchema',
    soWhat: 'This answers open question 1 from Rev 01: it is an annotation, not a gate. If it should hold the case, that is a change to make, not a drawing to correct.',
  },
  {
    id: 'd4', severity: 'medium',
    topic: 'Declining has two named choices',
    drawn: '"Decline repair — case closed — return or refurbish".',
    built: '`postDecline.choice` is `return_as_is` or `retain_and_close`, each with its own endpoint. Refurbishment is what follows retain-and-close, not an alternative to declining.',
    where: 'backend/routes/rma.js — decline-path/return, decline-path/close',
    soWhat: 'The customer is choosing whether they get the unit back, not choosing refurbishment.',
  },
  {
    id: 'd5', severity: 'medium',
    topic: 'Refurbishment is built, not planned',
    drawn: 'A black box marked "implement refurbished program here".',
    built: 'Decide scrap or repair, then per-task progress, cost, and recommission onto a new serial number with a link back to the originating case. Status runs pending → scrapped | repairing → recommissioned.',
    where: 'backend/routes/rma.js — refurbishment/decide, /task, /cost, /recommission',
    soWhat: 'Answers open question 4 from Rev 01 for the mechanism. What is still missing is the entry rule — which units qualify — and an owner.',
  },
  {
    id: 'd6', severity: 'medium',
    topic: 'Nine customer emails, not six',
    drawn: 'Six email annotations.',
    built: 'created, assigned, received, awaiting_repair, repairing, awaiting_shipping, shipped, archived and warranty_declined. Sent over Microsoft Graph, then mirrored onto the Odoo ticket as an internal note so the customer is not emailed twice.',
    where: 'backend/services/odooService.js — buildEmailBody templates',
    soWhat: 'The customer hears from us at every stage transition. Worth knowing before adding more notifications.',
  },
  {
    id: 'd7', severity: 'medium',
    topic: 'A case can hold several units',
    drawn: 'One unit per case throughout.',
    built: '`devices[]` with one flagged primary; each unit carries its own repair state (pending / repaired / scrapped / no_fault) and the case cannot leave `repairing` until all of them are finished. Cases can also absorb one another via `POST /rma/:id/bundle`, which keeps `sourceRmaId` so old references still resolve.',
    where: 'backend/models/RMA.js — rmaDeviceSchema; routes/rma.js — bundle, devices/repair',
    soWhat: 'Intake verification, the warranty decision and the outcome record all have to cope with more than one unit. Worth walking a two-unit case through the flow to check nothing assumes one.',
  },
  {
    id: 'd8', severity: 'low',
    topic: 'no_fault is a first-class outcome',
    drawn: 'Not shown.',
    built: 'A per-unit repair state alongside repaired and scrapped.',
    where: 'backend/models/RMA.js — RMA_DEVICE_REPAIR_STATES',
    soWhat: 'The most valuable outcome for prevention work — it usually points at documentation, commissioning or expectation rather than hardware.',
  },
  {
    id: 'd9', severity: 'low',
    topic: 'Satisfaction is already captured',
    drawn: 'Not shown.',
    built: '`POST /rma/archived/:id/satisfaction` stores a 0–10 score and a comment on the archived record.',
    where: 'backend/routes/rma.js — archived/:id/satisfaction',
    soWhat: 'The ISO gap raised against Flow A (#6.4) is already closed on the service side. Flow A can borrow the mechanism rather than invent one.',
  },
  {
    id: 'd10', severity: 'low',
    topic: 'Assignment is a status, not just an action',
    drawn: 'C2 assigns a repair location as a step.',
    built: '`assigned` is its own status with `assignedLocation`, and it gates the customer shipment declaration — `POST /customer-shipment` returns 422 unless the case is exactly at `assigned`, and 409 once a tracking number exists.',
    where: 'backend/routes/rma.js — customer-shipment',
    soWhat: 'Skipping the assigned stage would leave the customer unable to declare their shipment.',
  },
];

export const RMA_OPEN_QUESTIONS_REV2 = [
  {
    id: 'rq2-2',
    question: 'Should a shipment mismatch hold the case?',
    detail: 'Today it annotates and the repair proceeds. If a mismatch should pause work until the customer responds, that is a code change — the drawing implied a gate that was never built.',
  },
  {
    id: 'rq2-3',
    question: 'Who owns refurbishment, and what qualifies?',
    detail: 'The mechanism is complete. The missing pieces are the entry rule (which retained units are worth refurbishing) and a named owner. `/service/refurbished` already lists them.',
  },
  {
    id: 'rq2-4',
    question: 'Does anything in the flow still assume one unit per case?',
    detail: 'Multi-unit cases are supported end to end in the data model, but the warranty decision and the outcome record are held once per case, not per unit. Worth walking a two-unit case where one is covered and one is not.',
  },
];

// ===========================================================================
// What the process measures, and where it is captured.
//
// Read from the implementation. `capture: 'auto'` means the process produces the
// figure without anyone typing it; `capture: 'manual'` means a human has to
// classify something or the number never exists. That distinction is the whole
// point of this table — the automatic ones are safe, the manual ones are where
// the improvement data quietly goes missing.
// ===========================================================================

export const METRICS = {
  mttr: {
    id: 'mttr',
    short: 'MTTR',
    name: 'Mean time to repair',
    capture: 'auto',
    what: 'Days from the case existing to the unit going back out. Stored per case as `outcome.timeToRepairDays`; the mean is taken across cases.',
    how: 'computeTimeToRepair() takes the first `pending` or `assigned` entry in statusHistory and the last `shipped` or `archived` entry, and rounds to whole days.',
    where: 'backend/routes/rma.js — computeTimeToRepair, written on PATCH /rma/:id/outcome and at archive',
    surfaced: 'Per case in the archived record and the PDF report; exported to the learning corpus.',
    watch: 'The clock starts when the case is created, not when the unit arrives — so the customer\'s inbound shipping is inside the number. A same-day turnaround stores null rather than 0, because the code only keeps values greater than zero.',
  },
  mtbf: {
    id: 'mtbf',
    short: 'MTBF',
    name: 'Mean time between failures',
    capture: 'auto',
    what: 'Fleet operating hours divided by the number of accepted-warranty failures. Also broken down per product number and per Wave series.',
    how: 'GET /analytics/mtbf sums (now − scandate) in hours for every device in ProductionData, and divides by the count of RMAs where warranty was accepted (`warrantyBinary: true`, or the legacy `warrantyStatus` regex).',
    where: 'backend/routes/analytics.js — /analytics/mtbf, /analytics/mtbf-lifecycle',
    surfaced: '/analytics — MtbfAnalyticsCard and MtbfEachDeviceCard.',
    watch: 'Operating hours are calendar hours since the production scan, not powered-on hours. A unit sitting in a warehouse counts the same as one running 24/7, which inflates MTBF. There is a second, narrower MTBF per product family from `ProductMeta.failureData.tbfHistory` at /product-families/analytics — the two are not the same figure.',
  },
  warrantyRatio: {
    id: 'warrantyRatio',
    short: '< 2 / 1000',
    name: 'Warranty acceptance ratio',
    capture: 'auto',
    what: 'Accepted-warranty claims divided by devices currently within warranty. This is the target from the printed review, and it is already a live number.',
    how: 'computeWarrantyRatioFull() counts warranty-accepted RMAs whose device is still inside its warranty window and whose submission predates expiry, over the count of devices currently within warranty. Cached, with history, a PDF export and AI insights.',
    where: 'backend/routes/analytics.js — computeWarrantyRatioFull, /analytics/warranty-ratio(+/history, /history/pdf, /ai-insights)',
    surfaced: '/current-stats — against WARRANTY_RATIO_TARGET = 0.002 in warrantyUtils, which is exactly the < 2 per 1000 goal.',
  },
  warrantyBinary: {
    id: 'warrantyBinary',
    short: 'Warranty outcome',
    name: 'Did we accept liability?',
    capture: 'auto',
    what: 'One binary fact per closed case, derived rather than typed. It is the input to both MTBF and the warranty ratio.',
    how: 'Derived at archive from `repair.warranty.decision`, with `rootCause: no_fault_found` forcing false, then a legacy free-text fallback.',
    where: 'backend/routes/rma.js — warrantyBinary / finalWarrantyHandling derivation at archive',
    surfaced: 'ArchivedRma.warrantyBinary and finalWarrantyHandling; /analytics/rma byWarrantyStatus.',
    watch: 'See the defect below — the derivation tests for `declined` while the API records `denied`, so denied cases archive as null instead of false.',
  },
  deviceAge: {
    id: 'deviceAge',
    short: 'Age at failure',
    name: 'Device age when it failed',
    capture: 'auto',
    what: 'Days between the production scan and the RMA being submitted.',
    how: 'deviceAgeAtFailureDays computed at archive from the device scan date and `submittedAt`.',
    where: 'backend/routes/rma.js — archive',
    surfaced: 'ArchivedRma.deviceAgeAtFailureDays; feeds the MTBF lifecycle scatter.',
  },
  repeatFailure: {
    id: 'repeatFailure',
    short: 'Repeat failures',
    name: 'Prior RMAs on this unit',
    capture: 'auto',
    what: 'How many times this exact unit has been back before.',
    how: 'priorRmaCount counted at archive against ArchivedRma for the serial, including units bundled into other cases.',
    where: 'backend/routes/rma.js — archive',
    surfaced: 'ArchivedRma.priorRmaCount. A unit with a rising count is the strongest single signal of an unresolved root cause.',
  },
  rootCause: {
    id: 'rootCause',
    short: 'Root cause',
    name: 'Cause and resolution classification',
    capture: 'manual',
    what: 'Root cause, resolution type, parts replaced, failure mode, component age and technician hours.',
    how: 'PATCH /rma/:id/outcome. Only `timeToRepairDays` on that record is computed; every other field is entered by a person.',
    where: 'backend/routes/rma.js — PATCH /rma/:id/outcome; models/RMA.js outcomeSchema',
    surfaced: 'ArchivedRma.outcome, the PDF report, and the learning corpus.',
    watch: 'This is the one set that cannot be reconstructed later. Skip it and the case still closes, the customer is still happy, and the improvement loop gets nothing — the case becomes a number with no cause attached.',
  },
  satisfaction: {
    id: 'satisfaction',
    short: 'Satisfaction',
    name: 'Customer satisfaction score',
    capture: 'manual',
    what: 'A 0–10 score plus an optional comment, given by the customer after the case closes.',
    how: 'POST /rma/archived/:id/satisfaction, offered to the customer on the shipped/closed case.',
    where: 'backend/routes/rma.js — archived/:id/satisfaction',
    surfaced: 'ArchivedRma.satisfaction; exported to the learning corpus. Not currently rolled up on any dashboard.',
  },
  corpus: {
    id: 'corpus',
    short: 'Learning corpus',
    name: 'Case embedded for retrieval',
    capture: 'auto',
    what: 'The closed case becomes searchable prior art for the next similar fault.',
    how: 'On archive, the case is embedded and upserted to Pinecone when OpenAI and Pinecone keys are present.',
    where: 'backend/routes/rma.js — embedAndUpsert at archive; services/openaiService.js, services/pineconeService.js',
    surfaced: 'Retrieval over past cases; the training export at /service/training.',
    watch: 'Quality of retrieval depends on the manual outcome fields above. A case with no root cause embeds as a description with no answer in it.',
  },
  prematureClose: {
    id: 'prematureClose',
    short: 'Early closures',
    name: 'Cases closed before completion',
    capture: 'auto',
    what: 'Which stage a case was abandoned at, with the recorded reason and category.',
    how: 'POST /rma/:id/cancel stores cancellation.stage / category / reason and captures the case as learning data tagged `premature_closure`.',
    where: 'backend/routes/rma.js — cancel',
    surfaced: 'GET /rmas/cancellations; visible on the dashboard as "closed prematurely at <stage>".',
  },
  familyRate: {
    id: 'familyRate',
    short: 'RMA rate by family',
    name: 'Volume and RMA rate per product family',
    capture: 'auto',
    what: 'Production volume, RMA count and rate, and family MTBF, grouped by the live product family.',
    how: 'GET /product-families/analytics aggregates ProductionData volume against RMA counts and averages `failureData.tbfHistory`.',
    where: 'backend/routes/productMeta.js — /product-families/analytics',
    surfaced: 'Product family profiles; the per-family quality score.',
  },
};

// Which steps in the flow feed which metric. Kept as a lookup rather than a
// field on every node, so the node definitions stay readable.
export const NODE_METRICS = {
  // service loop
  'v.submit': ['mttr'],
  'v.s_pending': ['mttr'],
  'v.warranty': ['warrantyBinary', 'mtbf', 'warrantyRatio'],
  'v.deviceStates': ['rootCause'],
  'v.s_shipped': ['mttr'],
  'v.outcome': ['mttr', 'rootCause', 'deviceAge', 'repeatFailure'],
  'v.archive': ['warrantyBinary', 'deviceAge', 'repeatFailure', 'corpus', 'familyRate'],
  'v.satisfaction': ['satisfaction'],
  'v.feedback': ['mttr', 'mtbf', 'warrantyRatio', 'rootCause', 'satisfaction'],
  'v.cancel': ['prematureClose'],
  'v.refurb': ['rootCause'],
  // support loop — the close states that return a quantified result
  '6.0': ['mttr'],
  '6.1': ['mttr', 'warrantyRatio'],
  '6.2': ['mttr'],
  '6.3': ['rootCause'],
  '6.4': ['satisfaction'],
  '6.5': ['repeatFailure', 'familyRate'],
  '7.0': ['mttr', 'mtbf', 'warrantyRatio', 'rootCause', 'satisfaction'],
};

// Things the code does that nobody decided — defects and data-quality risks,
// not questions. Each was found by reading the implementation.
export const METRIC_FINDINGS = [
  {
    id: 'mf1',
    severity: 'high',
    kind: 'Defect',
    title: 'A denied warranty archives as "unknown", not as "denied"',
    detail: 'POST /rma/:id/repair/warranty accepts `accepted | denied`. The archive derivation tests `decision === \'declined\'`. Since nothing writes `declined`, a denied case falls through to the free-text fallback on an empty string and archives with `warrantyBinary: null` and `warrantyStatus: \'\'`.',
    effect: 'Denied cases are correctly excluded from MTBF failures, but they are also never counted as non-warranty claims — `nonWarrantyCount` and the /analytics/rma `byWarrantyStatus` breakdown both miss them, and `finalWarrantyHandling` stays empty instead of "Non-Warranty Claim".',
    fix: 'One word: accept `denied` (or both spellings) in the archive derivation. Worth doing deliberately rather than quietly — it changes historical reporting the moment it lands, and any already-archived denied cases stay null until backfilled.',
    where: 'backend/routes/rma.js — warrantyBinary derivation at archive vs repair/warranty validation',
  },
  {
    id: 'mf2',
    severity: 'high',
    kind: 'Data quality',
    title: 'The only fields that explain a failure are the manual ones',
    detail: 'MTTR, MTBF, the warranty ratio, device age, repeat count and the warranty binary are all derived. Root cause, resolution, failure mode, parts and technician hours are typed by a person on PATCH /rma/:id/outcome, and nothing blocks a case from closing without them.',
    effect: 'A case can complete perfectly and contribute nothing to the improvement loop. The counts stay right; the causes go missing, which is exactly the data the < 2 per 1000 target needs to act on.',
    fix: 'Either require root cause and resolution before archive, or report the completeness rate so the gap is visible. Not a process question — a validation choice.',
    where: 'backend/routes/rma.js — PATCH /rma/:id/outcome, archive',
  },
  {
    id: 'mf3',
    severity: 'medium',
    kind: 'Definition',
    title: 'MTBF counts calendar time, not operating time',
    detail: 'Fleet operating hours are computed as (now − scandate) for every device in the registry, regardless of whether it has ever been powered on.',
    effect: 'MTBF is really "mean calendar time between accepted-warranty failures across everything ever produced". It is a usable trend line but not a reliability figure, and it will drift upward on its own as the installed base ages.',
    fix: 'Fine as an internal trend. If it is ever quoted to a customer or a class society, say what it measures.',
    where: 'backend/routes/analytics.js — /analytics/mtbf operatingHours',
  },
  {
    id: 'mf4',
    severity: 'medium',
    kind: 'Definition',
    title: 'MTTR includes the customer\'s inbound shipping',
    detail: 'The clock starts at the first `pending` or `assigned` history entry — case creation — not at arrival. Return transit is excluded because it stops at `shipped`.',
    effect: 'The number is larger than a workshop turnaround figure and asymmetric: one transit leg is inside it and the other is not.',
    fix: 'Nothing to decide about how the system works. Just state the definition wherever the number appears, and if a workshop-only figure is wanted, `received → shipped` is already available from statusHistory.',
    where: 'backend/routes/rma.js — computeTimeToRepair',
  },
  {
    id: 'mf5',
    severity: 'medium',
    kind: 'Blind spot',
    title: 'Nothing reports cases that lost their Odoo link',
    detail: 'odooService.ensureTicket() is fire-and-forget on submit. If it fails or Odoo is unconfigured, the RMA is created with `odooTicketId: null` and no retry.',
    effect: 'The support loop silently stops seeing that case. Nothing counts how many cases are in that state.',
    fix: 'A count of RMAs with a null odooTicketId would make it visible; a retry would make it rare.',
    where: 'backend/routes/rma.js — POST /rma, notifyOdoo; services/odooService.js',
  },
  {
    id: 'mf6',
    severity: 'low',
    kind: 'Blind spot',
    title: 'Satisfaction is collected but never aggregated',
    detail: 'Scores are stored per archived case and exported to the learning corpus, but no endpoint or dashboard averages them.',
    effect: 'The one measure that reflects the customer instead of us cannot currently be trended.',
    fix: 'A mean and a distribution over archived cases would be a small addition to the existing analytics routes.',
    where: 'backend/routes/rma.js — archived/:id/satisfaction; no consumer in routes/analytics.js',
  },
  {
    id: 'mf7',
    severity: 'low',
    kind: 'Definition',
    title: 'Two different MTBF figures exist',
    detail: 'The fleet MTBF at /analytics/mtbf is computed from device scan dates and accepted-warranty RMAs. The per-family MTBF at /product-families/analytics is the average of `ProductMeta.failureData.tbfHistory`, a separately maintained field.',
    effect: 'Two numbers with the same name and different meanings, which will not agree.',
    fix: 'Label them distinctly wherever both are shown.',
    where: 'backend/routes/analytics.js vs backend/routes/productMeta.js',
  },
];

// Exported last, after both flows have registered their nodes into N.
export const NODES = N;
